/* eslint-disable max-len */
import {API_ENDPOINT_JSON} from '../../../../../config';
import {
  conditionType,
  inputType
} from '../../../../../pages/esgManager/interface/inputType';
import {COUNTRY_OPTIONS} from '../SME/smeQuestions';

import {
  DESCRIBE_YOUR_ORGANIZATION_OPTIONS,
  ENVIRONMENT_RESPONSIBILITIES_OF_THE_POSITION_OPTIONS,
  PRIMARY_REASONS_FOR_NOT_IMPLEMENTING_ENVIRONMENTAL_INITIATIVES_OPTIONS,
  PROCESS_PLACE_OPTIONS,
  TRADE_ASSOCIATION_OPTIONS
} from './corporateOptions';
import {CDP_CORPORATE_GOVERNANCE_FIELD_NAMES} from './corporateQuestionsKeys';

export const corporateGovernanceQuestions = [
  {
    key: '1',
    showIndex: false,
    items: [
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.1 Does your organization have a board of directors or an equivalent governing body?',
            tooltip:
              'This question provides context to data users on board composition and diversity, which have been found to correlate with performance on environmental issues'
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.RADIOGROUP,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_1_VALUE,
                  fieldLabel: 'Board of directors or equivalent governing body',
                  options: ['Yes', 'No'],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_2_VALUE,
                  fieldLabel:
                    'Frequency with which the board or equivalent meets',
                  options: [
                    {
                      label: 'More frequently than quarterly',
                      value: 'More frequently than quarterly'
                    },
                    {label: 'Quarterly', value: 'Quarterly'},
                    {label: 'Half-yearly', value: 'Half-yearly'},
                    {label: 'Annually', value: 'Annually'},
                    {
                      label: 'Less frequently than annually',
                      value: 'Less frequently than annually'
                    },
                    {
                      label: 'As important matters arise',
                      value: 'As important matters arise'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_3_VALUE,
                  fieldLabel:
                    'Types of directors your board or equivalent is comprised of',
                  options: [
                    {
                      label: 'Executive directors or equivalent',
                      value: 'Executive directors or equivalent'
                    },
                    {
                      label: 'Non-executive directors or equivalent',
                      value: 'Non-executive directors or equivalent'
                    },
                    {
                      label:
                        'Independent non-executive directors or equivalent',
                      value: 'Independent non-executive directors or equivalent'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_4_VALUE,
                  fieldLabel: 'Board diversity and inclusion policy',
                  options: [
                    {
                      label: 'Yes, and it is publicly available',
                      value: 'Yes, and it is publicly available'
                    },
                    {
                      label: 'Yes, but it is not publicly available',
                      value: 'Yes, but it is not publicly available'
                    },
                    {label: 'No', value: 'No'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_5_VALUE,
                  fieldLabel: 'Briefly describe what the policy covers',
                  colSpan: true,
                  textLimit: 1500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_4_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'Yes, and it is publicly available',
                      'Yes, but it is not publicly available'
                    ]
                  }
                },
                {
                  type: inputType.FILEUPLOAD,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_6_VALUE,
                  fieldLabel: 'Attach the policy (optional)',
                  colSpan: true,
                  fileUploadAction:
                    API_ENDPOINT_JSON.ESG.CDP_CORPORATE.documentUpload
                      .governance,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_4_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['No']
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.1.1 Is there board-level oversight of environmental issues within your organization?',
            tooltip: `To address environmental challenges, it is essential that organizations incorporate
                      environmental issues into their governance procedures. This question provides data users with an indication
                      of your level of commitment to addressing environmental issues in your organization`,
            conditionalVisibility: {
              dependsOn:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_VALUE,
              showWhen: ['Yes'],
              condition: conditionType.EQUALS,
              fieldName:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_1_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q2_VALUE,
            dependsOn:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_1_VALUE,
            showWhen: ['Yes'],
            relatedTableKey:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q2_1_VALUE,
                  fieldLabel: 'Environmental issue',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'},
                    {value: 'Biodiversity', label: 'Biodiversity'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q2_2_VALUE,
                  fieldLabel:
                    'Board-level oversight of this environmental issue',
                  options: PROCESS_PLACE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q2_3_VALUE,
                  fieldLabel:
                    'Primary reason for no boardlevel oversight of this environmental issue',
                  options:
                    PRIMARY_REASONS_FOR_NOT_IMPLEMENTING_ENVIRONMENTAL_INITIATIVES_OPTIONS,
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q2_2_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'No, but we plan to within the next two years',
                      'No, and we do not plan to within the next two years'
                    ]
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q2_4_VALUE,
                  fieldLabel:
                    'Explain why your organization does not have board-level oversight of this environmental issue',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q2_2_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'No, but we plan to within the next two years',
                      'No, and we do not plan to within the next two years'
                    ]
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.1.2 Identify the positions (do not include any names) of the individuals or committees on the board with accountability for environmental issues and provide details of the board’s oversight of environmental issues.',
            tooltip: `By disclosing information on governance mechanisms and board-level accountability
                    relating to environmental issues, organizations demonstrate the degree of strategic
                    significance attached to environmental issues by the organization. This question provides an indication of your level of commitment to 
                    highlighting the importance of environmental issues at the highest level of your organizational structure.`,
            conditionalVisibility: {
              dependsOn:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q2_VALUE,
              showWhen: ['Yes'],
              condition: conditionType.EQUALS,
              fieldName:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q2_2_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q3_VALUE,
            dependsOn:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q2_2_VALUE,
            showWhen: ['Yes'],
            relatedTableKey:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q2_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q3_1_VALUE,
                  fieldLabel: 'Environmental issue',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'},
                    {value: 'Biodiversity', label: 'Biodiversity'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q3_2_VALUE,
                  fieldLabel:
                    'Positions of individuals or committees with accountability for this environmental issue',
                  options: [
                    {label: 'Board chair', value: 'Board chair'},
                    {label: 'Director on board', value: 'Director on board'},
                    {
                      label: 'Chief Executive Officer (CEO)',
                      value: 'Chief Executive Officer (CEO)'
                    },
                    {
                      label: 'Chief Financial Officer (CFO)',
                      value: 'Chief Financial Officer (CFO)'
                    },
                    {
                      label: 'Chief Operating Officer (COO)',
                      value: 'Chief Operating Officer (COO)'
                    },
                    {
                      label: 'Chief Procurement Officer (CPO)',
                      value: 'Chief Procurement Officer (CPO)'
                    },
                    {
                      label: 'Chief Risk Officer (CRO)',
                      value: 'Chief Risk Officer (CRO)'
                    },
                    {
                      label: 'Chief Sustainability Officer (CSO)',
                      value: 'Chief Sustainability Officer (CSO)'
                    },
                    {
                      label:
                        'Chief Investment Officer (CIO) [Financial services only]',
                      value:
                        'Chief Investment Officer (CIO) [Financial services only]'
                    },
                    {
                      label:
                        'Chief Credit Officer (CCO) [Financial services only]',
                      value:
                        'Chief Credit Officer (CCO) [Financial services only]'
                    },
                    {
                      label:
                        'Chief Underwriting Officer (CUO) [Financial services only]',
                      value:
                        'Chief Underwriting Officer (CUO) [Financial services only]'
                    },
                    {
                      label: 'Chief Government Relations Officer (CGRO)',
                      value: 'Chief Government Relations Officer (CGRO)'
                    },
                    {
                      label: 'Chief Technology Officer (CTO)',
                      value: 'Chief Technology Officer (CTO)'
                    },
                    {
                      label: 'Chief Compliance Officer (CCO)',
                      value: 'Chief Compliance Officer (CCO)'
                    },
                    {
                      label: 'Other C-Suite Officer',
                      value: 'Other C-Suite Officer'
                    },
                    {
                      label: 'Board-level committee',
                      value: 'Board-level committee'
                    },
                    {label: 'President', value: 'President'},
                    {label: 'General Counsel', value: 'General Counsel'},
                    {
                      label: 'Other, please specify',
                      value: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.RADIOGROUP,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q3_3_VALUE,
                  fieldLabel:
                    'Positions’ accountability for this environmental issue is outlined in policies applicable to the board',
                  options: ['Yes', 'No'],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q3_4_VALUE,
                  fieldLabel:
                    'Policies which outline the positions’ accountability for this environmental issue',
                  options: [
                    {
                      label: 'Board Terms of Reference',
                      value: 'Board Terms of Reference'
                    },
                    {label: 'Board mandate', value: 'Board mandate'},
                    {
                      label: 'Individual role descriptions',
                      value: 'Individual role descriptions'
                    },
                    {
                      label:
                        'Other policy applicable to the board, please specify',
                      value:
                        'Other policy applicable to the board, please specify'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q3_3_VALUE,
                    condition: conditionType.NOT,
                    values: ['Yes']
                  }
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q3_5_VALUE,
                  fieldLabel:
                    'Frequency with which this environmental issue is a scheduled agenda item',
                  options: [
                    {
                      label:
                        'Scheduled agenda item in every board meeting (standing agenda item)',
                      value:
                        'Scheduled agenda item in every board meeting (standing agenda item)'
                    },
                    {
                      label:
                        'Scheduled agenda item in some board meetings – at least annually',
                      value:
                        'Scheduled agenda item in some board meetings – at least annually'
                    },
                    {
                      label:
                        'Scheduled agenda item in some board meetings – less than annually',
                      value:
                        'Scheduled agenda item in some board meetings – less than annually'
                    },
                    {
                      label:
                        'Sporadic – agenda item as important matters arise',
                      value: 'Sporadic – agenda item as important matters arise'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q3_6_VALUE,
                  fieldLabel:
                    'Governance mechanisms into which this environmental issue is integrated',
                  options: [
                    {
                      label: 'Overseeing and guiding scenario analysis',
                      value: 'Overseeing and guiding scenario analysis'
                    },
                    {
                      label:
                        'Reviewing and guiding the assessment process for dependencies, impacts, risks, and opportunities',
                      value:
                        'Reviewing and guiding the assessment process for dependencies, impacts, risks, and opportunities'
                    },
                    {
                      label:
                        'Overseeing reporting, audit, and verification processes',
                      value:
                        'Overseeing reporting, audit, and verification processes'
                    },
                    {
                      label: 'Approving corporate policies and/or commitments',
                      value: 'Approving corporate policies and/or commitments'
                    },
                    {
                      label:
                        'Monitoring compliance with corporate policies and/or commitments',
                      value:
                        'Monitoring compliance with corporate policies and/or commitments'
                    },
                    {
                      label: 'Overseeing the setting of corporate targets',
                      value: 'Overseeing the setting of corporate targets'
                    },
                    {
                      label: 'Monitoring progress towards corporate targets',
                      value: 'Monitoring progress towards corporate targets'
                    },
                    {
                      label:
                        'Monitoring supplier compliance with organizational requirements',
                      value:
                        'Monitoring supplier compliance with organizational requirements'
                    },
                    {
                      label: 'Overseeing and guiding public policy engagement',
                      value: 'Overseeing and guiding public policy engagement'
                    },
                    {
                      label: 'Overseeing and guiding value chain engagement',
                      value: 'Overseeing and guiding value chain engagement'
                    },
                    {
                      label:
                        'Overseeing and guiding the development of a climate transition plan [CC only]',
                      value:
                        'Overseeing and guiding the development of a climate transition plan [CC only]'
                    },
                    {
                      label:
                        'Monitoring the implementation of a climate transition plan [CC only]',
                      value:
                        'Monitoring the implementation of a climate transition plan [CC only]'
                    },
                    {
                      label:
                        'Overseeing and guiding the development of a business strategy',
                      value:
                        'Overseeing and guiding the development of a business strategy'
                    },
                    {
                      label:
                        'Monitoring the implementation of the business strategy',
                      value:
                        'Monitoring the implementation of the business strategy'
                    },
                    {
                      label:
                        'Overseeing and guiding acquisitions, mergers, and divestitures',
                      value:
                        'Overseeing and guiding acquisitions, mergers, and divestitures'
                    },
                    {
                      label:
                        'Overseeing and guiding major capital expenditures',
                      value: 'Overseeing and guiding major capital expenditures'
                    },
                    {
                      label: 'Reviewing and guiding annual budgets',
                      value: 'Reviewing and guiding annual budgets'
                    },
                    {
                      label: 'Reviewing and guiding innovation/R&D priorities',
                      value: 'Reviewing and guiding innovation/R&D priorities'
                    },
                    {
                      label: 'Approving and/or overseeing employee incentives',
                      value: 'Approving and/or overseeing employee incentives'
                    },
                    {
                      label: 'Other, please specify',
                      value: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q3_7_VALUE,
                  fieldLabel: 'Scope of board-level oversight',
                  options: [
                    {
                      label: 'Risks and opportunities to our own operations',
                      value: 'Risks and opportunities to our own operations'
                    },
                    {
                      label:
                        'Risks and opportunities to our banking activities',
                      value: 'Risks and opportunities to our banking activities'
                    },
                    {
                      label:
                        'Risks and opportunities to our investment activities',
                      value:
                        'Risks and opportunities to our investment activities'
                    },
                    {
                      label:
                        'Risks and opportunities to our insurance underwriting activities',
                      value:
                        'Risks and opportunities to our insurance underwriting activities'
                    },
                    {
                      label:
                        'The impact of our own operations on the environment',
                      value:
                        'The impact of our own operations on the environment'
                    },
                    {
                      label:
                        'The impact of our banking activities on the environment',
                      value:
                        'The impact of our banking activities on the environment'
                    },
                    {
                      label:
                        'The impact of our investing activities on the environment',
                      value:
                        'The impact of our investing activities on the environment'
                    },
                    {
                      label:
                        'The impact of our insurance underwriting activities on the environment',
                      value:
                        'The impact of our insurance underwriting activities on the environment'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q3_8_VALUE,
                  fieldLabel: 'Please explain',
                  colSpan: true,
                  textLimit: 3000
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.2 Does your organization’s board have competency on environmental issues?',
            tooltip:
              'Transitioning a business for success in a sustainable future requires environmental competency within its decision-making bodies. This capability at board-level and commitment to maintaining high levels of competency signals an organization’s commitment to understanding and responding to environmental dependencies, impacts, risks, and opportunities.',
            conditionalVisibility: {
              dependsOn:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_VALUE,
              showWhen: ['Yes'],
              condition: conditionType.EQUALS,
              fieldName:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_1_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q4_VALUE,
            dependsOn:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_1_VALUE,
            showWhen: ['Yes'],
            relatedTableKey:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q4_1_VALUE,
                  fieldLabel: 'Environmental issue',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q4_2_VALUE,
                  fieldLabel:
                    'Board-level competency on this environmental issue',
                  options: [
                    {label: 'Yes', value: 'Yes'},
                    {
                      label: 'No, but we plan to within the next two years',
                      value: 'No, but we plan to within the next two years'
                    },
                    {
                      label:
                        'No, and we do not plan to within the next two years',
                      value:
                        'No, and we do not plan to within the next two years'
                    },
                    {label: 'Not assessed', value: 'Not assessed'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT_WITH_OTHER_TEXTBOX,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q4_3_VALUE,
                  fieldLabel:
                    'Mechanisms to maintain an environmentally competent board',
                  options: [
                    {
                      label:
                        'Consulting regularly with an internal, permanent, subject-expert working group',
                      value:
                        'Consulting regularly with an internal, permanent, subject-expert working group'
                    },
                    {
                      label:
                        'Engaging regularly with external stakeholders and experts on environmental issues',
                      value:
                        'Engaging regularly with external stakeholders and experts on environmental issues'
                    },
                    {
                      label:
                        'Integrating knowledge of environmental issues into board nominating process',
                      value:
                        'Integrating knowledge of environmental issues into board nominating process'
                    },
                    {
                      label:
                        'Regular training for directors on environmental issues, industry best practice, and standards (e.g., TCFD, SBTi)',
                      value:
                        'Regular training for directors on environmental issues, industry best practice, and standards (e.g., TCFD, SBTi)'
                    },
                    {
                      label:
                        'Having at least one board member with expertise on this environmental issue',
                      value:
                        'Having at least one board member with expertise on this environmental issue'
                    },
                    {
                      label: 'Other, please specify',
                      value: 'Other, please specify'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q4_2_VALUE,
                    condition: conditionType.NOT,
                    values: ['Yes']
                  }
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q4_4_VALUE,
                  fieldLabel: 'Environmental expertise of the board member',
                  options: [
                    {
                      label:
                        'Undergraduate education (e.g., BSc/BA in environment and sustainability, climate science, environmental science, water resources management, environmental engineering, forestry, etc.), please specify',
                      value:
                        'Undergraduate education (e.g., BSc/BA in environment and sustainability, climate science, environmental science, water resources management, environmental engineering, forestry, etc.), please specify'
                    },
                    {
                      label:
                        'Postgraduate education (e.g., MSc/MA/PhD in environment and sustainability, climate science, environmental science, water resources management, forestry, etc.), please specify',
                      value:
                        'Postgraduate education (e.g., MSc/MA/PhD in environment and sustainability, climate science, environmental science, water resources management, forestry, etc.), please specify'
                    },
                    {
                      label:
                        'Course certificate (relating to environmental issues), please specify',
                      value:
                        'Course certificate (relating to environmental issues), please specify'
                    },
                    {
                      label:
                        'Training in an environmental subject by a certified organization, please specify',
                      value:
                        'Training in an environmental subject by a certified organization, please specify'
                    },
                    {
                      label:
                        'Executive-level experience in a role focused on environmental issues',
                      value:
                        'Executive-level experience in a role focused on environmental issues'
                    },
                    {
                      label:
                        'Management-level experience in a role focused on environmental issues',
                      value:
                        'Management-level experience in a role focused on environmental issues'
                    },
                    {
                      label:
                        'Staff-level experience in a role focused on environmental issues',
                      value:
                        'Staff-level experience in a role focused on environmental issues'
                    },
                    {
                      label:
                        'Experience in an academic role focused on environmental issues',
                      value:
                        'Experience in an academic role focused on environmental issues'
                    },
                    {
                      label:
                        'Experience in the environmental department of a government (national or local)',
                      value:
                        'Experience in the environmental department of a government (national or local)'
                    },
                    {
                      label:
                        'Experience in an organization that is exposed to environmental-scrutiny and is going through a sustainability transition',
                      value:
                        'Experience in an organization that is exposed to environmental-scrutiny and is going through a sustainability transition'
                    },
                    {
                      label:
                        'Active member of an environmental committee or organization',
                      value:
                        'Active member of an environmental committee or organization'
                    },
                    {
                      label: 'Other, please specify',
                      value: 'Other, please specify'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q4_3_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: [
                      'Consulting regularly with an internal, permanent, subject-expert working group',
                      'Engaging regularly with external stakeholders and experts on environmental issues',
                      'Integrating knowledge of environmental issues into board nominating process',
                      'Regular training for directors on environmental issues, industry best practice, and standards (e.g., TCFD, SBTi)',
                      'Having at least one board member with expertise on this environmental issue'
                    ]
                  }
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q4_5_VALUE,
                  fieldLabel:
                    'Primary reason for no board-level competency on this environmental issue',
                  options:
                    PRIMARY_REASONS_FOR_NOT_IMPLEMENTING_ENVIRONMENTAL_INITIATIVES_OPTIONS,
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q4_2_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Yes', 'Not assessed']
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q4_6_VALUE,
                  fieldLabel:
                    'Explain why your organization does not have a board with competence on this environmental issue ',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q4_2_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Yes', 'Not assessed']
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.3 Is there management-level responsibility for environmental issues within your organization?',
            tooltip:
              'While it is most important for a member of the board to have accountability over environmental issues, assigning management-level responsibility indicates that the organization is committed to implementing its environmental policy. '
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q5_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q5_1_VALUE,
                  fieldLabel: 'Environmental issue',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'},
                    {value: 'Biodiversity', label: 'Biodiversity'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q5_2_VALUE,
                  fieldLabel:
                    'Management-level responsibility for this environmental issue',
                  options: PROCESS_PLACE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q5_3_VALUE,
                  fieldLabel:
                    'Primary reason for no management-level responsibility for environmental issues',
                  options:
                    PRIMARY_REASONS_FOR_NOT_IMPLEMENTING_ENVIRONMENTAL_INITIATIVES_OPTIONS,
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q5_2_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'No, but we plan to within the next two years',
                      'No, and we do not plan to within the next two years'
                    ]
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q5_4_VALUE,
                  fieldLabel:
                    'Explain why your organization does not have management-level responsibility for environmental issues',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q5_2_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'No, but we plan to within the next two years',
                      'No, and we do not plan to within the next two years'
                    ]
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.3.1 Provide the highest senior management-level positions or committees with responsibility for environmental issues (do not include the names of individuals).',
            tooltip: `While it is most important for a member of the board to have accountability over
                      environmental issues, assigning management-level responsibility indicates that the
                      organization is committed to implementing its environmental policy.`,
            conditionalVisibility: {
              dependsOn:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q5_VALUE,
              showWhen: ['Yes'],
              condition: conditionType.EQUALS,
              fieldName:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q5_2_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q6_VALUE,
            dependsOn:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q5_2_VALUE,
            showWhen: ['Yes'],
            relatedTableKey:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q5_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q6_1_VALUE,
                  fieldLabel: 'Environmental issue',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'},
                    {value: 'Biodiversity', label: 'Biodiversity'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q6_2_VALUE,
                  fieldLabel:
                    'Position of individual or committee with responsibility',
                  options: [
                    {
                      label: 'Chief Executive Officer (CEO)',
                      value: 'Chief Executive Officer (CEO)'
                    },
                    {
                      label: 'Chief Financial Officer (CFO)',
                      value: 'Chief Financial Officer (CFO)'
                    },
                    {
                      label: 'Chief Operating Officer (COO)',
                      value: 'Chief Operating Officer (COO)'
                    },
                    {
                      label: 'Chief Procurement Officer (CPO)',
                      value: 'Chief Procurement Officer (CPO)'
                    },
                    {
                      label: 'Chief Risks Officer (CRO)',
                      value: 'Chief Risks Officer (CRO)'
                    },
                    {
                      label: 'Chief Sustainability Officer (CSO)',
                      value: 'Chief Sustainability Officer (CSO)'
                    },
                    {
                      label: 'Chief Investment Officer (CIO) [FS only]',
                      value: 'Chief Investment Officer (CIO) [FS only]'
                    },
                    {
                      label: 'Chief Credit Officer (CCO) [FS only]',
                      value: 'Chief Credit Officer (CCO) [FS only]'
                    },
                    {
                      label: 'Chief Underwriting Officer (CUO) [FS only]',
                      value: 'Chief Underwriting Officer (CUO) [FS only]'
                    },
                    {
                      label: 'Chief Government Relations Officer (CGRO)',
                      value: 'Chief Government Relations Officer (CGRO)'
                    },
                    {
                      label: 'Chief Technology Officer (CTO)',
                      value: 'Chief Technology Officer (CTO)'
                    },
                    {
                      label: 'Chief Compliance Officer (CCO)',
                      value: 'Chief Compliance Officer (CCO)'
                    },
                    {
                      label: 'Other C-Suite Officer, please specify',
                      value: 'Other C-Suite Officer, please specify'
                    },
                    {label: 'President', value: 'President'},
                    {label: 'General Counsel', value: 'General Counsel'},
                    {label: 'Risk committee', value: 'Risk committee'},
                    {
                      label: 'Sustainability committee',
                      value: 'Sustainability committee'
                    },
                    {
                      label: 'Environmental, Social, Governance committee',
                      value: 'Environmental, Social, Governance committee'
                    },
                    {
                      label:
                        'Safety, Health, Environment and Quality committee',
                      value: 'Safety, Health, Environment and Quality committee'
                    },
                    {
                      label: 'Corporate responsibility committee',
                      value: 'Corporate responsibility committee'
                    },
                    {
                      label: 'Credit committee [FS only]',
                      value: 'Credit committee [FS only]'
                    },
                    {
                      label: 'Investment committee [FS only]',
                      value: 'Investment committee [FS only]'
                    },
                    {
                      label: 'Responsible Investment committee [FS only]',
                      value: 'Responsible Investment committee [FS only]'
                    },
                    {
                      label: 'Audit committee [FS only]',
                      value: 'Audit committee [FS only]'
                    },
                    {
                      label: 'Other committee, please specify',
                      value: 'Other committee, please specify'
                    },
                    {
                      label: 'Other position, please specify',
                      value: 'Other position, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q6_3_VALUE,
                  fieldLabel: 'Environmental responsibilities of this position',
                  options: ENVIRONMENT_RESPONSIBILITIES_OF_THE_POSITION_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q6_4_VALUE,
                  fieldLabel: 'Coverage of responsibilities',
                  options: [
                    {
                      label:
                        'Dependencies, impacts, risks, and opportunities related to our banking activities',
                      value:
                        'Dependencies, impacts, risks, and opportunities related to our banking activities'
                    },
                    {
                      label:
                        'Dependencies, impacts, risks, and opportunities related to our investing activities',
                      value:
                        'Dependencies, impacts, risks, and opportunities related to our investing activities'
                    },
                    {
                      label:
                        'Dependencies, impacts, risks, and opportunities related to our insurance underwriting activities',
                      value:
                        'Dependencies, impacts, risks, and opportunities related to our insurance underwriting activities'
                    },
                    {
                      label:
                        'Dependencies, impacts, risks and opportunities related to our own operations and/or upstream value chain',
                      value:
                        'Dependencies, impacts, risks and opportunities related to our own operations and/or upstream value chain'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q6_3_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: [
                      'Assessing future trends in environmental dependencies, impacts, risks, and opportunities',
                      'Assessing environmental dependencies, impacts, risks, and opportunities',
                      'Managing environmental dependencies, impacts, risks, and opportunities'
                    ]
                  }
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q6_5_VALUE,
                  fieldLabel: 'Reporting line ',
                  options: [
                    {
                      label: 'Reports to the board directly',
                      value: 'Reports to the board directly'
                    },
                    {
                      label: 'Reports to the Chief Executive Officer (CEO)',
                      value: 'Reports to the Chief Executive Officer (CEO)'
                    },
                    {
                      label: 'Reports to the Chief Financial Officer (CFO)',
                      value: 'Reports to the Chief Financial Officer (CFO)'
                    },
                    {
                      label: 'Reports to the Chief Operating Officer (COO)',
                      value: 'Reports to the Chief Operating Officer (COO)'
                    },
                    {
                      label: 'Reports to the Chief Risks Officer (CRO)',
                      value: 'Reports to the Chief Risks Officer (CRO)'
                    },
                    {
                      label:
                        'Reports to the Chief Sustainability Officer (CSO)',
                      value: 'Reports to the Chief Sustainability Officer (CSO)'
                    },
                    {
                      label:
                        'Reports to the Chief Investment Officer (CIO) [FS only]',
                      value:
                        'Reports to the Chief Investment Officer (CIO) [FS only]'
                    },
                    {
                      label: 'Other, please specify',
                      value: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q6_6_VALUE,
                  fieldLabel:
                    'Frequency of reporting to the board on environmental issues',
                  options: [
                    {
                      label: 'More frequently than quarterly',
                      value: 'More frequently than quarterly'
                    },
                    {label: 'Quarterly', value: 'Quarterly'},
                    {label: 'Half-yearly', value: 'Half-yearly'},
                    {label: 'Annually', value: 'Annually'},
                    {
                      label: 'Less frequently than annually',
                      value: 'Less frequently than annually'
                    },
                    {
                      label: 'As important matters arise',
                      value: 'As important matters arise'
                    },
                    {
                      label: 'Not reported to the board',
                      value: 'Not reported to the board'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q6_7_VALUE,
                  fieldLabel: 'Please explain',
                  colSpan: true,
                  textLimit: 2000
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.4 Does your organization have management-level competency on environmental issues?',
            tooltip: `Transitioning a business for success in a sustainable future requires environmental
                      competency within its decision-making bodies. This capability at management-level for
                      organizations without a board signals an organization’s commitment to understanding and responding to environmental dependencies, impacts, risks, and opportunities. `,
            conditionalVisibility: {
              dependsOn:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_VALUE,
              showWhen: ['No'],
              condition: conditionType.EQUALS,
              fieldName:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_1_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q7_VALUE,
            dependsOn:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_1_VALUE,
            showWhen: ['No'],
            relatedTableKey:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q1_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q7_1_VALUE,
                  fieldLabel: 'Environmental issue',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q7_2_VALUE,
                  fieldLabel:
                    'Managementlevel competency on this environmental issue',
                  options: [
                    {label: 'Yes', value: 'Yes'},
                    {
                      label: 'No, but we plan to within the next two years',
                      value: 'No, but we plan to within the next two years'
                    },
                    {
                      label:
                        'No, and we do not plan to within the next two years',
                      value:
                        'No, and we do not plan to within the next two years'
                    },
                    {label: 'Not assessed', value: 'Not assessed'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT_WITH_OTHER_TEXTBOX,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q7_3_VALUE,
                  fieldLabel:
                    'Mechanisms to maintain management level competency on this environmental issue',
                  options: [
                    {
                      label:
                        'Consulting regularly with an internal, permanent, subject-expert working group',
                      value:
                        'Consulting regularly with an internal, permanent, subject-expert working group'
                    },
                    {
                      label:
                        'Engaging regularly with external stakeholders and experts on environmental issues',
                      value:
                        'Engaging regularly with external stakeholders and experts on environmental issues'
                    },
                    {
                      label:
                        'Regular training at management level on environmental issues, industry best practice, and standards (e.g., TCFD, SBTi)',
                      value:
                        'Regular training at management level on environmental issues, industry best practice, and standards (e.g., TCFD, SBTi)'
                    },
                    {
                      label:
                        'Having at least one individual at management level with expertise on this environmental issue',
                      value:
                        'Having at least one individual at management level with expertise on this environmental issue'
                    },
                    {
                      label: 'Other, please specify',
                      value: 'Other, please specify'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q7_2_VALUE,
                    condition: conditionType.NOT,
                    values: ['Yes']
                  }
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q7_4_VALUE,
                  fieldLabel:
                    'Environmental expertise of the individual at management level',
                  options: [
                    {
                      label:
                        'Undergraduate education (e.g., BSc/BA in environment and sustainability, climate science, environmental science, water resources management, environmental engineering, forestry, etc.)',
                      value: 'Yes, specify: BSc in Environmental Science'
                    },
                    {
                      label:
                        'Postgraduate education (e.g., MSc/MA/PhD in environment and sustainability, climate science, environmental science, water resources management, forestry, etc.)',
                      value: 'Yes, specify: MSc in Climate Science'
                    },
                    {
                      label:
                        'Course certificate (relating to environmental issues), please specify',
                      value:
                        'Yes, specify: Certificate in Environmental Impact Assessment'
                    },
                    {
                      label:
                        'Training in an environmental subject by a certified organization, please specify',
                      value:
                        'Yes, specify: Training in Sustainable Supply Chain Management by recognized NGO'
                    },
                    {
                      label:
                        'Executive-level experience in a role focused on environmental issues',
                      value: 'No'
                    },
                    {
                      label:
                        'Management-level experience in a role focused on environmental issues',
                      value: 'Yes'
                    },
                    {
                      label:
                        'Staff-level experience in a role focused on environmental issues',
                      value: 'Yes'
                    },
                    {
                      label:
                        'Experience in an academic role focused on environmental issues',
                      value: 'No'
                    },
                    {
                      label:
                        'Experience in the environmental department of a government (national or local)',
                      value: 'No'
                    },
                    {
                      label:
                        'Experience in an organization that is exposed to environmental-scrutiny and is going through a sustainability transition',
                      value: 'Yes'
                    },
                    {
                      label:
                        'Active member of an environmental committee or organization',
                      value: 'No'
                    },
                    {
                      label: 'Other, please specify',
                      value:
                        'Experience in corporate ESG reporting and supply chain sustainability analysis'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q7_3_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: [
                      'Consulting regularly with an internal, permanent, subject-expert working group',
                      'Engaging regularly with external stakeholders and experts on environmental issues',
                      'Regular training at management level on environmental issues, industry best practice, and standards (e.g., TCFD, SBTi)',
                      'Having at least one individual at management level with expertise on this environmental issue'
                    ]
                  }
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q7_5_VALUE,
                  fieldLabel:
                    'Primary reason for no managementlevel competency on environmental issues',
                  options:
                    PRIMARY_REASONS_FOR_NOT_IMPLEMENTING_ENVIRONMENTAL_INITIATIVES_OPTIONS,
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q7_2_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Yes', 'Not assessed']
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q7_6_VALUE,
                  fieldLabel:
                    'Explain why your organization does not have managementlevel competency on this environmental issue',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q7_2_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Yes', 'Not assessed']
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.5 Do you provide monetary incentives for the management of environmental issues, including the attainment of targets? ',
            tooltip:
              'Employee incentives linked to sustainability performance send a clear signal about the role that employees are expected to play in achieving the organization’s environmental commitments and targets. Data users aim to understand the degree to which organizations encourage their employees to address environmental issues, as well as the mechanisms by which certain behaviors are incentivized in the organization’s decision making and business culture. '
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q8_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q8_1_VALUE,
                  fieldLabel: 'Environmental issue',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'},
                    {
                      value: 'Biodiversity [M-B only]',
                      label: 'Biodiversity [M-B only]'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q8_2_VALUE,
                  fieldLabel:
                    'Provision of monetary incentives related to this environmental issue',
                  options: [
                    {label: 'Yes', value: 'Yes'},
                    {
                      label:
                        'No, but we plan to introduce them in the next two years',
                      value: 'No, but we plan to within the next two years'
                    },
                    {
                      label:
                        'No, and we do not plan to introduce them in the next two years',
                      value:
                        'No, and we do not plan to within the next two years'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q8_3_VALUE,
                  fieldLabel:
                    '% of total C-suite and board-level monetary incentives linked to the management of this environmental issue',
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q8_2_VALUE,
                    condition: conditionType.NOT,
                    values: ['Yes']
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q8_4_VALUE,
                  fieldLabel: 'Please explain',
                  colSpan: true,
                  textLimit: 1000,
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q8_2_VALUE,
                    condition: conditionType.EQUALS,
                    values: [
                      'No, and we do not plan to within the next two years',
                      'No, but we plan to within the next two years',
                      'Yes'
                    ],
                    required: true,
                    dependentValue:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q8_2_VALUE
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.5.1 Provide further details on the monetary incentives provided for the management of environmental issues (do not include the names of individuals). ',
            tooltip:
              'Employee incentives linked to sustainability performance send a clear signal about the role that employees are expected to play in achieving the organization’s environmental commitments and targets. Data users aim to understand the degree to which organizations encourage their employees to address environmental issues, as well as the mechanisms by which certain behaviors are incentivized in the organization’s decision making and business culture. Providing insight into these incentives and the specific performance metrics used to address environmental issues informs data users how these incentives are aligned with the identification, assessment, and management of the organization’s environmental dependencies, impacts, risks, and opportunities. ',
            conditionalVisibility: {
              dependsOn:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q8_VALUE,
              showWhen: ['Yes'],
              condition: conditionType.EQUALS,
              fieldName:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q8_2_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q9_VALUE,
            dependsOn:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q8_2_VALUE,
            showWhen: ['Yes'],
            relatedTableKey:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q8_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q9_1_VALUE,
                  fieldLabel: 'Environmental issue',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'},
                    {
                      value: 'Biodiversity [M-B only]',
                      label: 'Biodiversity [M-B only]'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q9_2_VALUE,
                  fieldLabel: 'Position entitled to monetary incentive ',
                  options: [
                    {value: 'Board Chair', label: 'Board Chair'},
                    {
                      value: 'Board/Executive board',
                      label: 'Board/Executive board'
                    },
                    {
                      value: 'Chief Compliance Officer (CCO)',
                      label: 'Chief Compliance Officer (CCO)'
                    },
                    {
                      value: 'Chief Credit Officer (CCO) [FS only]',
                      label: 'Chief Credit Officer (CCO) [FS only]'
                    },
                    {
                      value: 'Chief Executive Officer (CEO)',
                      label: 'Chief Executive Officer (CEO)'
                    },
                    {
                      value: 'Chief Financial Officer (CFO)',
                      label: 'Chief Financial Officer (CFO)'
                    },
                    {
                      value: 'Chief Government Relations Officer (CGRO)',
                      label: 'Chief Government Relations Officer (CGRO)'
                    },
                    {
                      value: 'Chief Investment Officer (CIO) [FS only]',
                      label: 'Chief Investment Officer (CIO) [FS only]'
                    },
                    {
                      value: 'Chief Operating Officer (COO)',
                      label: 'Chief Operating Officer (COO)'
                    },
                    {
                      value: 'Chief Procurement Officer (CPO)',
                      label: 'Chief Procurement Officer (CPO)'
                    },
                    {
                      value: 'Chief Risks Officer (CRO)',
                      label: 'Chief Risks Officer (CRO)'
                    },
                    {
                      value: 'Chief Sustainability Officer (CSO)',
                      label: 'Chief Sustainability Officer (CSO)'
                    },
                    {
                      value: 'Chief Technology Officer (CTO)',
                      label: 'Chief Technology Officer (CTO)'
                    },
                    {
                      value: 'Chief Underwriting Officer (CUO) [FS only]',
                      label: 'Chief Underwriting Officer (CUO) [FS only]'
                    },
                    {
                      value: 'Corporate executive team',
                      label: 'Corporate executive team'
                    },
                    {value: 'Director on board', label: 'Director on board'},
                    {
                      value: 'Other C-Suite Officer, please specify',
                      label: 'Other C-Suite Officer, please specify'
                    },
                    {value: 'General Counsel', label: 'General Counsel'},
                    {value: 'President', label: 'President'},
                    {
                      value: 'Senior-mid management',
                      label: 'Senior-mid management'
                    },
                    {value: 'Buyers/purchasers', label: 'Buyers/purchasers'},
                    {value: 'Energy manager', label: 'Energy manager'},
                    {
                      value: 'Environment/Sustainability manager',
                      label: 'Environment/Sustainability manager'
                    },
                    {
                      value: 'Environmental, Health, and Safety manager',
                      label: 'Environmental, Health, and Safety manager'
                    },
                    {value: 'Management group', label: 'Management group'},
                    {
                      value: 'Process operation manager',
                      label: 'Process operation manager'
                    },
                    {
                      value: 'Procurement manager',
                      label: 'Procurement manager'
                    },
                    {
                      value: 'Public affairs manager',
                      label: 'Public affairs manager'
                    },
                    {value: 'Risk manager', label: 'Risk manager'},
                    {
                      value: 'ESG Portfolio/Fund manager [FS only]',
                      label: 'ESG Portfolio/Fund manager [FS only]'
                    },
                    {
                      value: 'Investor relations manager [FS only]',
                      label: 'Investor relations manager [FS only]'
                    },
                    {
                      value: 'Portfolio/Fund manager [FS only]',
                      label: 'Portfolio/Fund manager [FS only]'
                    },
                    {
                      value: 'Other senior-mid manager, please specify',
                      label: 'Other senior-mid manager, please specify'
                    },
                    {
                      value: 'Facility/Unit/Site management',
                      label: 'Facility/Unit/Site management'
                    },
                    {
                      value: 'Business unit manager',
                      label: 'Business unit manager'
                    },
                    {value: 'Facilities manager', label: 'Facilities manager'},
                    {value: 'Site manager', label: 'Site manager'},
                    {
                      value: 'Other facility/unit/site manager, please specify',
                      label: 'Other facility/unit/site manager, please specify'
                    },
                    {
                      value: 'Sustainability specialist',
                      label: 'Sustainability specialist'
                    },
                    {
                      value:
                        'Dedicated responsible investment analyst [FS only]',
                      label:
                        'Dedicated responsible investment analyst [FS only]'
                    },
                    {
                      value: 'Investment/credit/insurance analyst [FS only]',
                      label: 'Investment/credit/insurance analyst [FS only]'
                    },
                    {
                      value: 'Risk analyst [FS only]',
                      label: 'Risk analyst [FS only]'
                    },
                    {
                      value: 'Other sustainability specialist, please specify',
                      label: 'Other sustainability specialist, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q9_3_VALUE,
                  fieldLabel: 'Incentives',
                  options: [
                    {
                      value: 'Bonus - % of salary',
                      label: 'Bonus - % of salary'
                    },
                    {value: 'Bonus – set figure', label: 'Bonus – set figure'},
                    {value: 'Promotion', label: 'Promotion'},
                    {value: 'Salary increase', label: 'Salary increase'},
                    {value: 'Shares', label: 'Shares'},
                    {value: 'Profit share', label: 'Profit share'},
                    {value: 'Retirement plan', label: 'Retirement plan'},
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q9_4_VALUE,
                  fieldLabel: 'Performance metrics  ',
                  options: [
                    {
                      value: 'Progress towards environmental targets',
                      label: 'Progress towards environmental targets'
                    },
                    {
                      value: 'Achievement of environmental targets',
                      label: 'Achievement of environmental targets'
                    },
                    {
                      value:
                        'Organization performance against an environmental sustainability index',
                      label:
                        'Organization performance against an environmental sustainability index'
                    },
                    {
                      value:
                        'Reduction in absolute emissions in line with net-zero target [CC only]',
                      label:
                        'Reduction in absolute emissions in line with net-zero target [CC only]'
                    },
                    {
                      value: 'Other targets-related metrics, please specify',
                      label: 'Other targets-related metrics, please specify'
                    },
                    {
                      value: 'Strategy and financial planning',
                      label: 'Strategy and financial planning'
                    },
                    {
                      value:
                        'Board approval of climate transition plan [CC only]',
                      label:
                        'Board approval of climate transition plan [CC only]'
                    },
                    {
                      value:
                        'Shareholder approval of climate transition plan [CC only]',
                      label:
                        'Shareholder approval of climate transition plan [CC only]'
                    },
                    {
                      value: 'Achievement of climate transition plan [CC only]',
                      label: 'Achievement of climate transition plan [CC only]'
                    },
                    {
                      value:
                        'Increased green asset ratio of portfolio/fund [FS only]',
                      label:
                        'Increased green asset ratio of portfolio/fund [FS only]'
                    },
                    {
                      value:
                        'Shift to a business model compatible with a net-zero carbon future [CC only]',
                      label:
                        'Shift to a business model compatible with a net-zero carbon future [CC only]'
                    },
                    {
                      value:
                        'Increased investment in environmental R&D and innovation',
                      label:
                        'Increased investment in environmental R&D and innovation'
                    },
                    {
                      value:
                        'Increased proportion of revenue from low environmental impact products or services',
                      label:
                        'Increased proportion of revenue from low environmental impact products or services'
                    },
                    {
                      value:
                        'Increased alignment of capex with transition plan and/or sustainable finance taxonomy [CC only]',
                      label:
                        'Increased alignment of capex with transition plan and/or sustainable finance taxonomy [CC only]'
                    },
                    {
                      value:
                        'Other strategy and financial planning-related metrics, please specify',
                      label:
                        'Other strategy and financial planning-related metrics, please specify'
                    },
                    {value: 'Emission reduction', label: 'Emission reduction'},
                    {
                      value:
                        'Implementation of an emissions reduction initiative [CC only]',
                      label:
                        'Implementation of an emissions reduction initiative [CC only]'
                    },
                    {
                      value: 'Reduction in emissions intensity [CC only]',
                      label: 'Reduction in emissions intensity [CC only]'
                    },
                    {
                      value:
                        'Increased share of renewable energy in total energy consumption [CC only]',
                      label:
                        'Increased share of renewable energy in total energy consumption [CC only]'
                    },
                    {
                      value: 'Reduction in absolute emissions [CC only]',
                      label: 'Reduction in absolute emissions [CC only]'
                    },
                    {
                      value:
                        'Emissions reductions across portfolio companies [FS only]',
                      label:
                        'Emissions reductions across portfolio companies [FS only]'
                    },
                    {
                      value:
                        'Other emission reduction-related metrics, please specify [CC only]',
                      label:
                        'Other emission reduction-related metrics, please specify [CC only]'
                    },
                    {
                      value: 'Resource use and efficiency',
                      label: 'Resource use and efficiency'
                    },
                    {
                      value:
                        'Reduction of virgin wood fiber used in paper and packaging products [F only]',
                      label:
                        'Reduction of virgin wood fiber used in paper and packaging products [F only]'
                    },
                    {
                      value:
                        'Eliminating deforestation and conversion of other natural ecosystems [F only]',
                      label:
                        'Eliminating deforestation and conversion of other natural ecosystems [F only]'
                    },
                    {
                      value:
                        'Reduction of water withdrawals – direct operations [W only]',
                      label:
                        'Reduction of water withdrawals – direct operations [W only]'
                    },
                    {
                      value:
                        'Improvements in water efficiency – direct operations [W only]',
                      label:
                        'Improvements in water efficiency – direct operations [W only]'
                    },
                    {
                      value:
                        'Improvements in emissions data, reporting, and verification [CC only]',
                      label:
                        'Improvements in emissions data, reporting, and verification [CC only]'
                    },
                    {
                      value: 'Energy efficiency improvement [CC only]',
                      label: 'Energy efficiency improvement [CC only]'
                    },
                    {
                      value: 'Reduction in total energy consumption [CC only]',
                      label: 'Reduction in total energy consumption [CC only]'
                    },
                    {
                      value:
                        'Other resource use and efficiency-related metrics, please specify',
                      label:
                        'Other resource use and efficiency-related metrics, please specify'
                    },
                    {value: 'Pollution', label: 'Pollution'},
                    {
                      value: 'Improvements in wastewater quality [W only]',
                      label: 'Improvements in wastewater quality [W only]'
                    },
                    {
                      value: 'Reduction of water pollution incidents [W only]',
                      label: 'Reduction of water pollution incidents [W only]'
                    },
                    {
                      value:
                        'Reduction or phase out of hazardous substances [W only]',
                      label:
                        'Reduction or phase out of hazardous substances [W only]'
                    },
                    {
                      value:
                        'Increase in discharge treatment compliance [W only]',
                      label:
                        'Increase in discharge treatment compliance [W only]'
                    },
                    {
                      value:
                        'Reduction/elimination of environmental incidents/notices of violation',
                      label:
                        'Reduction/elimination of environmental incidents/notices of violation'
                    },
                    {
                      value: 'Other pollution-related metrics, please specify',
                      label: 'Other pollution-related metrics, please specify'
                    },
                    {
                      value: 'Policies and commitments',
                      label: 'Policies and commitments'
                    },
                    {
                      value:
                        'Increased supplier compliance with environmental requirements',
                      label:
                        'Increased supplier compliance with environmental requirements'
                    },
                    {
                      value:
                        'New or tighter environmental requirements applied to purchasing practices',
                      label:
                        'New or tighter environmental requirements applied to purchasing practices'
                    },
                    {
                      value:
                        'Restoration and compensation to address past deforestation [F only]',
                      label:
                        'Restoration and compensation to address past deforestation [F only]'
                    },
                    {
                      value:
                        'Implementation of water-related community projects [W only]',
                      label:
                        'Implementation of water-related community projects [W only]'
                    },
                    {
                      value:
                        'Increased access to workplace WASH – direct operations [W only]',
                      label:
                        'Increased access to workplace WASH – direct operations [W only]'
                    },
                    {
                      value:
                        'Other policies and commitments-related metrics, please specify',
                      label:
                        'Other policies and commitments-related metrics, please specify'
                    },
                    {value: 'Engagement', label: 'Engagement'},
                    {
                      value:
                        'Increased engagement with suppliers or clients on environmental issues',
                      label:
                        'Increased engagement with suppliers or clients on environmental issues'
                    },
                    {
                      value: 'Increased engagement with smallholders [F only]',
                      label: 'Increased engagement with smallholders [F only]'
                    },
                    {
                      value:
                        'Increased engagement in landscapes or jurisdictions [F/W]',
                      label:
                        'Increased engagement in landscapes or jurisdictions [F/W]'
                    },
                    {
                      value:
                        'Increased value chain visibility (traceability, mapping)',
                      label:
                        'Increased value chain visibility (traceability, mapping)'
                    },
                    {
                      value:
                        'Implementation of employee awareness campaign/training',
                      label:
                        'Implementation of employee awareness campaign/training'
                    },
                    {
                      value: 'Other engagement-related metrics, please specify',
                      label: 'Other engagement-related metrics, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q9_5_VALUE,
                  fieldLabel: 'Incentive plan the incentives are linked to',
                  options: [
                    {
                      value:
                        'Short-Term Incentive Plan, or equivalent, only (e.g. contractual annual bonus)',
                      label:
                        'Short-Term Incentive Plan, or equivalent, only (e.g. contractual annual bonus)'
                    },
                    {
                      value:
                        'Long-Term Incentive Plan, or equivalent, only (e.g. contractual multi-year bonus)',
                      label:
                        'Long-Term Incentive Plan, or equivalent, only (e.g. contractual multi-year bonus)'
                    },
                    {
                      value:
                        'Both Short-Term and Long-Term Incentive Plan, or equivalent',
                      label:
                        'Both Short-Term and Long-Term Incentive Plan, or equivalent'
                    },
                    {
                      value:
                        'The incentives are not linked to an incentive plan, or equivalent (e.g. discretionary bonus in the reporting year)',
                      label:
                        'The incentives are not linked to an incentive plan, or equivalent (e.g. discretionary bonus in the reporting year)'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q9_6_VALUE,
                  fieldLabel: 'Further details of incentives',
                  colSpan: true,
                  textLimit: 2500
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q9_7_VALUE,
                  fieldLabel:
                    'How the position’s incentives contribute to the achievement of your environmental commitments and/or climate transition plan',
                  colSpan: true,
                  textLimit: 2500
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.6 Does your organization have an environmental policy that addresses environmental issues?',
            tooltip: `An environmental policy is a key governance tool which serves as a foundation to drive action while ensuring accountability. Setting a corporate environmental policy for environmental issues indicates that the organization understands their importance and recognizes its responsibility in taking environmental action. Therefore, data users wish to know that organizations have articulated and documented a policy that acknowledges the organization’s dependencies, impacts, risks, and opportunities
                      associated with environmental issues, and have clear intentions and direction for action. For accountability to the organization’s stakeholders, the policy should be publicly available.`
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q10_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q10_1_VALUE,
                  fieldLabel:
                    'Does your organization have any environmental policies?',
                  options: PROCESS_PLACE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q10_2_VALUE,
                  fieldLabel:
                    'Primary reason for not having an environmental policy',
                  options:
                    PRIMARY_REASONS_FOR_NOT_IMPLEMENTING_ENVIRONMENTAL_INITIATIVES_OPTIONS,
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q10_1_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'No, but we plan to within the next two years',
                      'No, and we do not plan to within the next two years'
                    ]
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q10_3_VALUE,
                  fieldLabel:
                    'Explain why you do not have an environmental policy',
                  colSpan: true,
                  textLimit: 2500
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading: '4.6.1 Provide details of your environmental policies.',
            tooltip:
              'An environmental policy is a key governance tool which serves as a foundation to drive environmental action while ensuring accountability. This question informs data users about the organization’s commitments regarding environmental issues by providing an insight into its level of awareness of those issues and demonstrating the robustness of actions outlined in the policy. ',
            conditionalVisibility: {
              dependsOn:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q10_VALUE,
              showWhen: ['Yes'],
              condition: conditionType.EQUALS,
              fieldName:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q10_1_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q11_VALUE,
            dependsOn:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q10_1_VALUE,
            showWhen: ['Yes'],
            relatedTableKey:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q10_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q11_1_VALUE,
                  fieldLabel: 'Environmental issues covered',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'},
                    {value: 'Biodiversity', label: 'Biodiversity'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q11_2_VALUE,
                  fieldLabel: 'Level of coverage',
                  options: [
                    {value: 'Organization-wide', label: 'Organization-wide'},
                    {
                      value:
                        'Selected facilities, businesses or geographies only',
                      label:
                        'Selected facilities, businesses or geographies only'
                    },
                    {
                      value: 'Selected products only',
                      label: 'Selected products only'
                    },
                    {
                      value:
                        'Selected commodities only [appears only if “Forests” is selected in column 1]',
                      label:
                        'Selected commodities only [appears only if “Forests” is selected in column 1]'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q11_3_VALUE,
                  fieldLabel: 'Value chain stages covered',
                  options: [
                    {value: 'Direct operations', label: 'Direct operations'},
                    {
                      value: 'Upstream value chain',
                      label: 'Upstream value chain'
                    },
                    {
                      value: 'Downstream value chain',
                      label: 'Downstream value chain'
                    },
                    {value: 'Portfolio [FS only]', label: 'Portfolio [FS only]'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q11_4_VALUE,
                  fieldLabel: 'Explain the coverage',
                  colSpan: true,
                  textLimit: 1500
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q11_5_VALUE,
                  fieldLabel: 'Environmental policy content',
                  options: [
                    {
                      value: 'Commitment to a circular economy strategy',
                      label: 'Commitment to a circular economy strategy'
                    },
                    {
                      value:
                        'Commitment to avoidance of negative impacts on threatened and protected species [appears only if B, F, or W is selected in C1]',
                      label:
                        'Commitment to avoidance of negative impacts on threatened and protected species [appears only if B, F, or W is selected in C1]'
                    },
                    {
                      value:
                        'Commitment to comply with regulations and mandatory standards',
                      label:
                        'Commitment to comply with regulations and mandatory standards'
                    },
                    {
                      value:
                        'Commitment to take environmental action beyond regulatory compliance',
                      label:
                        'Commitment to take environmental action beyond regulatory compliance'
                    },
                    {
                      value:
                        'Commitment to engage in integrated, multi-stakeholder landscape (including river basin) initiatives to promote shared sustainability goals [appears only if F or W is selected in C1]',
                      label:
                        'Commitment to engage in integrated, multi-stakeholder landscape (including river basin) initiatives to promote shared sustainability goals [appears only if F or W is selected in C1]'
                    },
                    {
                      value:
                        'Commitment to implementation of nature-based solutions that support landscape restoration and long-term protection of natural ecosystems',
                      label:
                        'Commitment to implementation of nature-based solutions that support landscape restoration and long-term protection of natural ecosystems'
                    },
                    {
                      value:
                        'Commitment to Net Positive Gain [appears only if B, F, or W is selected in C1]',
                      label:
                        'Commitment to Net Positive Gain [appears only if B, F, or W is selected in C1]'
                    },
                    {
                      value:
                        'Commitment to No Net Loss [appears only if B, F, or W is selected in C1]',
                      label:
                        'Commitment to No Net Loss [appears only if B, F, or W is selected in C1]'
                    },
                    {
                      value:
                        'Commitment to no trade of CITES listed species [appears only if B or F is selected in C1]',
                      label:
                        'Commitment to no trade of CITES listed species [appears only if B or F is selected in C1]'
                    },
                    {
                      value:
                        'Commitment to respect legally designated protected areas [appears only if B, F, or W is selected in C1]',
                      label:
                        'Commitment to respect legally designated protected areas [appears only if B, F, or W is selected in C1]'
                    },
                    {
                      value:
                        'Commitment to stakeholder engagement and capacity building on environmental issues',
                      label:
                        'Commitment to stakeholder engagement and capacity building on environmental issues'
                    },
                    {
                      value: 'Other environmental commitment, please specify',
                      label: 'Other environmental commitment, please specify'
                    },
                    {
                      value:
                        'Commitment to 100% renewable energy [appears if “Climate change” is selected in column 1]',
                      label:
                        'Commitment to 100% renewable energy [appears if “Climate change” is selected in column 1]'
                    },
                    {
                      value:
                        'Commitment to net-zero emissions [appears if “Climate change” is selected in column 1]',
                      label:
                        'Commitment to net-zero emissions [appears if “Climate change” is selected in column 1]'
                    },
                    {
                      value:
                        'Commitment to not invest in fossil-fuel expansion [appears if “Climate change” is selected in column 1]',
                      label:
                        'Commitment to not invest in fossil-fuel expansion [appears if “Climate change” is selected in column 1]'
                    },
                    {
                      value:
                        'Commitment to zero flaring [CC-OG/CO only] [appears if “Climate change” is selected in column 1]',
                      label:
                        'Commitment to zero flaring [CC-OG/CO only] [appears if “Climate change” is selected in column 1]'
                    },
                    {
                      value:
                        'Commitment to not funding climate-denial or lobbying against climate regulations [appears if “Climate change” is selected in column 1]',
                      label:
                        'Commitment to not funding climate-denial or lobbying against climate regulations [appears if “Climate change” is selected in column 1]'
                    },
                    {
                      value: 'Other climate-related commitment, please specify',
                      label: 'Other climate-related commitment, please specify'
                    },
                    {
                      value:
                        'Commitment to best management practices for soils and peat [appears if “Forests” is selected in column 1]',
                      label:
                        'Commitment to best management practices for soils and peat [appears if “Forests” is selected in column 1]'
                    },
                    {
                      value:
                        'Commitment to conduct or support restoration and/or compensation to remedy for past deforestation or conversion [appears if “Forests” is selected in column 1]',
                      label:
                        'Commitment to conduct or support restoration and/or compensation to remedy for past deforestation or conversion [appears if “Forests” is selected in column 1]'
                    },
                    {
                      value:
                        'Commitment to facilitate the inclusion of smallholders into the value chain [appears if “Forests” is selected in column 1]',
                      label:
                        'Commitment to facilitate the inclusion of smallholders into the value chain [appears if “Forests” is selected in column 1]'
                    },
                    {
                      value:
                        'Commitment to no deforestation, to no planting on peatlands, and to no exploitation (NDPE) by target date, please specify [appears if “Forests” is selected in column 1]',
                      label:
                        'Commitment to no deforestation, to no planting on peatlands, and to no exploitation (NDPE) by target date, please specify [appears if “Forests” is selected in column 1]'
                    },
                    {
                      value:
                        'Commitment to no development on peat regardless of depth [appears if “Forests” is selected in column 1]',
                      label:
                        'Commitment to no development on peat regardless of depth [appears if “Forests” is selected in column 1]'
                    },
                    {
                      value:
                        'Commitment to no land clearance by burning or clear-cutting [appears if “Forests” is selected in column 1]',
                      label:
                        'Commitment to no land clearance by burning or clear-cutting [appears if “Forests” is selected in column 1]'
                    },
                    {
                      value:
                        'Commitment to no-conversion of natural ecosystems by target date, please specify [appears if “Forests” is selected in column 1]',
                      label:
                        'Commitment to no-conversion of natural ecosystems by target date, please specify [appears if “Forests” is selected in column 1]'
                    },
                    {
                      value:
                        'Commitment to reduce or phase out hazardous substances [appears if “Water” is selected in column 1]',
                      label:
                        'Commitment to reduce or phase out hazardous substances [appears if “Water” is selected in column 1]'
                    },
                    {
                      value:
                        'Commitment to control/reduce/eliminate water pollution [appears if “Water” is selected in column 1]',
                      label:
                        'Commitment to control/reduce/eliminate water pollution [appears if “Water” is selected in column 1]'
                    },
                    {
                      value:
                        'Commitment to reduce water consumption volumes [appears if “Water” is selected in column 1]',
                      label:
                        'Commitment to reduce water consumption volumes [appears if “Water” is selected in column 1]'
                    },
                    {
                      value:
                        'Commitment to reduce water withdrawal volumes [appears if “Water” is selected in column 1]',
                      label:
                        'Commitment to reduce water withdrawal volumes [appears if “Water” is selected in column 1]'
                    },
                    {
                      value:
                        'Commitment to safely managed WASH in local communities [appears if “Water” is selected in column 1]',
                      label:
                        'Commitment to safely managed WASH in local communities [appears if “Water” is selected in column 1]'
                    },
                    {
                      value:
                        'Commitment to the conservation of freshwater ecosystems [appears if “Water” is selected in column 1]',
                      label:
                        'Commitment to the conservation of freshwater ecosystems [appears if “Water” is selected in column 1]'
                    },
                    {
                      value:
                        'Commitment to water stewardship and/or collective action [appears if “Water” is selected in column 1]',
                      label:
                        'Commitment to water stewardship and/or collective action [appears if “Water” is selected in column 1]'
                    },
                    {
                      value: 'Other water-related commitment, please specify',
                      label: 'Other water-related commitment, please specify'
                    },
                    {
                      value:
                        'Adoption of the UN International Labour Organization principles',
                      label:
                        'Adoption of the UN International Labour Organization principles'
                    },
                    {
                      value:
                        'Commitment to promote gender equality and women’s empowerment',
                      label:
                        'Commitment to promote gender equality and women’s empowerment'
                    },
                    {
                      value:
                        'Commitment to respect and protect the customary rights to land, resources, and territory of Indigenous Peoples and Local Communities',
                      label:
                        'Commitment to respect and protect the customary rights to land, resources, and territory of Indigenous Peoples and Local Communities'
                    },
                    {
                      value:
                        'Commitment to respect internationally recognized human rights',
                      label:
                        'Commitment to respect internationally recognized human rights'
                    },
                    {
                      value:
                        'Commitment to secure Free, Prior, and Informed Consent (FPIC) of Indigenous Peoples and local communities',
                      label:
                        'Commitment to secure Free, Prior, and Informed Consent (FPIC) of Indigenous Peoples and local communities'
                    },
                    {
                      value: 'Other social commitment, please specify',
                      label: 'Other social commitment, please specify'
                    },
                    {
                      value:
                        'Acknowledgement of the human right to water and sanitation [appears if “Water” is selected]',
                      label:
                        'Acknowledgement of the human right to water and sanitation [appears if “Water” is selected]'
                    },
                    {
                      value:
                        'Description of biodiversity-related performance standards [appears if “Biodiversity” is selected]',
                      label:
                        'Description of biodiversity-related performance standards [appears if “Biodiversity” is selected]'
                    },
                    {
                      value:
                        'Description of commodities covered by the policy [appears if “Forests” is selected]',
                      label:
                        'Description of commodities covered by the policy [appears if “Forests” is selected]'
                    },
                    {
                      value:
                        'Description of dependencies on natural resources and ecosystems',
                      label:
                        'Description of dependencies on natural resources and ecosystems'
                    },
                    {
                      value:
                        'Description of impacts on natural resources and ecosystems',
                      label:
                        'Description of impacts on natural resources and ecosystems'
                    },
                    {
                      value:
                        'Description of environmental requirements for procurement',
                      label:
                        'Description of environmental requirements for procurement'
                    },
                    {
                      value:
                        'Description of grievance/whistleblower mechanism to monitor non-compliance with the environmental policy and raise/address/escalate any other greenwashing concerns',
                      label:
                        'Description of grievance/whistleblower mechanism to monitor non-compliance with the environmental policy and raise/address/escalate any other greenwashing concerns'
                    },
                    {
                      value:
                        'Description of membership and financial support provided to organizations that seek to influence public policy',
                      label:
                        'Description of membership and financial support provided to organizations that seek to influence public policy'
                    },
                    {
                      value:
                        'Description of renewable electricity procurement practices [appears if “Climate change” is selected]',
                      label:
                        'Description of renewable electricity procurement practices [appears if “Climate change” is selected]'
                    },
                    {
                      value:
                        'Recognition of environmental linkages and trade-offs',
                      label:
                        'Recognition of environmental linkages and trade-offs'
                    },
                    {
                      value:
                        'Reference to timebound environmental milestones and targets',
                      label:
                        'Reference to timebound environmental milestones and targets'
                    },
                    {
                      value:
                        'Other additional reference/description, please specify',
                      label:
                        'Other additional reference/description, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q11_6_VALUE,
                  fieldLabel:
                    'Indicate whether your environmental policy is in line with global environmental treaties or policy goals',
                  options: [
                    {
                      value:
                        'Yes, in line with the Paris Agreement [appears if “Climate change” is selected in column 1]',
                      label:
                        'Yes, in line with the Paris Agreement [appears if “Climate change” is selected in column 1]'
                    },
                    {
                      value:
                        'Yes, in line with the Kunming-Montreal Global Biodiversity Framework [appears if “Biodiversity”, “Forests” or “Water” is selected in column 1]',
                      label:
                        'Yes, in line with the Kunming-Montreal Global Biodiversity Framework [appears if “Biodiversity”, “Forests” or “Water” is selected in column 1]'
                    },
                    {
                      value:
                        'Yes, in line with Sustainable Development Goal 6 on Clean Water and Sanitation [appears if “Water” is selected in column 1]',
                      label:
                        'Yes, in line with Sustainable Development Goal 6 on Clean Water and Sanitation [appears if “Water” is selected in column 1]'
                    },
                    {
                      value:
                        'Yes, in line with another global environmental treaty or policy goal, please specify',
                      label:
                        'Yes, in line with another global environmental treaty or policy goal, please specify'
                    },
                    {
                      value: 'No, but we plan to align in the next two years',
                      label: 'No, but we plan to align in the next two years'
                    },
                    {
                      value:
                        'No, and we do not plan to align in the next two years',
                      label:
                        'No, and we do not plan to align in the next two years'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q11_7_VALUE,
                  fieldLabel: 'Public availability',
                  options: [
                    {value: 'Publicly available', label: 'Publicly available'},
                    {
                      value: 'Not publicly available',
                      label: 'Not publicly available'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.FILEUPLOAD,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q11_8_VALUE,
                  fieldLabel: 'Attach the policy',
                  colSpan: true,
                  fileUploadAction:
                    API_ENDPOINT_JSON.ESG.CDP_CORPORATE.documentUpload
                      .governance
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q12_VALUE,
            heading:
              '4.7 Does the policy framework for the portfolio activities of your organization include environmental requirements that clients/investees need to meet, and/or exclusion policies?',
            tooltip:
              'Considering environmental issues in an organization’s policy framework is an important element of business strategy and a signal of how deeply environmental issues are embedded in an organization’s processes. For these reasons, data users are interested in understanding whether organizations in the financial services sector have integrated environmental requirements for clients/investees, and whether financial institutions have implemented any environmental exclusion policies.'
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q12_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q12_1_VALUE,
                  fieldLabel: 'Portfolio',
                  options: [
                    {value: 'Banking (Bank)', label: 'Banking (Bank)'},
                    {
                      value: 'Investing (Asset manager)',
                      label: 'Investing (Asset manager)'
                    },
                    {
                      value: 'Investing (Asset owner)',
                      label: 'Investing (Asset owner)'
                    },
                    {
                      value: 'Insurance (Insurance company)',
                      label: 'Insurance (Insurance company)'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q12_2_VALUE,
                  fieldLabel:
                    'Policy framework for portfolio activities include environmental requirements for clients/investees, and/or exclusion policies',
                  options: [
                    {
                      value:
                        'Yes, our policies include environmental requirements that clients/investees need to meet',
                      label:
                        'Yes, our policies include environmental requirements that clients/investees need to meet'
                    },
                    {
                      value:
                        'Yes, we have exclusion policies for industries, activities and/or locations exposed or contributing to environmental risks',
                      label:
                        'Yes, we have exclusion policies for industries, activities and/or locations exposed or contributing to environmental risks'
                    },
                    {
                      value:
                        'Yes, our framework includes both policies with environmental client/investee requirements and environmental exclusion policies',
                      label:
                        'Yes, our framework includes both policies with environmental client/investee requirements and environmental exclusion policies'
                    },
                    {
                      value:
                        'No, but we plan to include environmental requirements and/or exclusion policies in our policy framework in the next two years',
                      label:
                        'No, but we plan to include environmental requirements and/or exclusion policies in our policy framework in the next two years'
                    },
                    {
                      value:
                        'No, and we do not plan to include environmental requirements and/or exclusion policies in our policy framework in the next two years',
                      label:
                        'No, and we do not plan to include environmental requirements and/or exclusion policies in our policy framework in the next two years'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q12_3_VALUE,
                  fieldLabel:
                    'Primary reason for not including both policies with environmental client/investee requirements and environmental exclusion policies in your policy framework for portfolio activities',
                  options:
                    PRIMARY_REASONS_FOR_NOT_IMPLEMENTING_ENVIRONMENTAL_INITIATIVES_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q12_4_VALUE,
                  fieldLabel:
                    'Explain why the policy framework for your portfolio activities does not include both policies with environmental client/investee requirements and environmental exclusion policies',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q12_2_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'No, but we plan to include environmental requirements and/or exclusion policies in our policy framework in the next two years',
                      'No, and we do not plan to include environmental requirements and/or exclusion policies in our policy framework in the next two years'
                    ]
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.7.1 Provide details of the policies which include environmental requirements that clients/investees need to meet.',
            tooltip:
              'To help manage environmental risks, organizations should integrate environmental issues into existing policy frameworks. These policies may apply across the organization and may be based on sectors, geographies, business lines, asset classes or other. Although the wave of environmental policies and regulations is growing, their implementation varies across organizations. This question helps data users understand which corporate policies integrate environmental issues, and what proportion of a financial services company’s portfolio is covered by the policy.',
            conditionalVisibility: {
              dependsOn:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q12_VALUE,
              showWhen: [
                'Yes, our policies include environmental requirements that clients/investees need to meet',
                'Yes, our framework includes both policies with environmental client/investee requirements and environmental exclusion policies'
              ],
              condition: conditionType.EQUALS,
              fieldName:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q12_2_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_VALUE,
            dependsOn:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q12_2_VALUE,
            showWhen: [
              'Yes, our policies include environmental requirements that clients/investees need to meet',
              'Yes, our framework includes both policies with environmental client/investee requirements and environmental exclusion policies'
            ],
            relatedTableKey:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q12_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_1_VALUE,
                  fieldLabel: 'Portfolio',
                  options: [
                    {value: 'Banking (Bank)', label: 'Banking (Bank)'},
                    {
                      value: 'Investing (Asset manager)',
                      label: 'Investing (Asset manager)'
                    },
                    {
                      value: 'Investing (Asset owner)',
                      label: 'Investing (Asset owner)'
                    },
                    {
                      value: 'Insurance (Insurance company)',
                      label: 'Insurance (Insurance company)'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_2_VALUE,
                  fieldLabel: 'Environmental issues covered',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'},
                    {value: 'Biodiversity', label: 'Biodiversity'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_3_VALUE,
                  fieldLabel: 'Type of policy',
                  options: [
                    // Banking
                    {
                      value: 'Credit/lending policy',
                      label: 'Credit/lending policy'
                    },
                    {value: 'Risk policy', label: 'Risk policy'},
                    {
                      value: 'Underwriting policy',
                      label: 'Underwriting policy'
                    },
                    {value: 'Engagement policy', label: 'Engagement policy'},
                    {value: 'Pricing policy', label: 'Pricing policy'},
                    {
                      value: 'Other banking policy, please specify',
                      label: 'Other banking policy, please specify'
                    },

                    // Investing
                    {value: 'Credit policy', label: 'Credit policy'},
                    {value: 'Risk policy', label: 'Risk policy'},
                    {
                      value: 'Sustainable/Responsible Investment Policy',
                      label: 'Sustainable/Responsible Investment Policy'
                    },
                    {
                      value: 'Investment policy/strategy',
                      label: 'Investment policy/strategy'
                    },
                    {value: 'Stewardship policy', label: 'Stewardship policy'},
                    {
                      value: 'Active ownership policy',
                      label: 'Active ownership policy'
                    },
                    {value: 'Pricing policy', label: 'Pricing policy'},
                    {
                      value: 'Other investing policy, please specify',
                      label: 'Other investing policy, please specify'
                    },

                    // Insurance
                    {value: 'Risk policy', label: 'Risk policy'},
                    {
                      value: 'Insurance underwriting policy',
                      label: 'Insurance underwriting policy'
                    },
                    {value: 'Engagement policy', label: 'Engagement policy'},
                    {value: 'Pricing policy', label: 'Pricing policy'},
                    {
                      value: 'Other insurance policy, please specify',
                      label: 'Other insurance policy, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_4_VALUE,
                  fieldLabel: 'Public availability',
                  options: [
                    {value: 'Publicly available', label: 'Publicly available'},
                    {
                      value: 'Not publicly available',
                      label: 'Not publicly available'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.FILEUPLOAD,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_5_VALUE,
                  fieldLabel: 'Attach the policy',
                  colSpan: true,
                  fileUploadAction:
                    API_ENDPOINT_JSON.ESG.CDP_CORPORATE.documentUpload
                      .governance
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_6_VALUE,
                  fieldLabel:
                    'Value chain stages of client/investee covered by policy',
                  options: [
                    {value: 'Direct operations', label: 'Direct operations'},
                    {
                      value:
                        'Direct operations and upstream/downstream value chain',
                      label:
                        'Direct operations and upstream/downstream value chain'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_7_VALUE,
                  fieldLabel: 'Industry sectors covered by the policy',
                  options: [
                    {value: 'Apparel', label: 'Apparel'},
                    {
                      value: 'Biotech, health care & pharma',
                      label: 'Biotech, health care & pharma'
                    },
                    {
                      value: 'Food, beverage & agriculture',
                      label: 'Food, beverage & agriculture'
                    },
                    {value: 'Fossil Fuels', label: 'Fossil Fuels'},
                    {value: 'Hospitality', label: 'Hospitality'},
                    {value: 'Infrastructure', label: 'Infrastructure'},
                    {
                      value: 'International bodies',
                      label: 'International bodies'
                    },
                    {value: 'Manufacturing', label: 'Manufacturing'},
                    {value: 'Materials', label: 'Materials'},
                    {value: 'Power generation', label: 'Power generation'},
                    {value: 'Retail', label: 'Retail'},
                    {value: 'Services', label: 'Services'},
                    {
                      value: 'Transportation services',
                      label: 'Transportation services'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_8_VALUE,
                  fieldLabel: 'Commodities covered by the policy',
                  options: [
                    {
                      value: 'All agricultural commodities',
                      label: 'All agricultural commodities'
                    },
                    {value: 'Timber products', label: 'Timber products'},
                    {value: 'Palm oil', label: 'Palm oil'},
                    {value: 'Cattle products', label: 'Cattle products'},
                    {value: 'Soy', label: 'Soy'},
                    {value: 'Rubber', label: 'Rubber'},
                    {value: 'Cocoa', label: 'Cocoa'},
                    {value: 'Coffee', label: 'Coffee'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_9_VALUE,
                  fieldLabel:
                    'Commodity value chain stage covered by the policy',
                  options: [
                    {value: 'Production', label: 'Production'},
                    {value: 'Processing', label: 'Processing'},
                    {value: 'Trading', label: 'Trading'},
                    {value: 'Manufacturing', label: 'Manufacturing'},
                    {value: 'Retailing', label: 'Retailing'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_10_VALUE,
                  fieldLabel:
                    '% of portfolio covered by the policy in relation to total portfolio value ',
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_11_VALUE,
                  fieldLabel: 'Basis of exceptions to policy',
                  options: [
                    {value: 'Geography', label: 'Geography'},
                    {value: 'Subsidiaries', label: 'Subsidiaries'},
                    {value: 'Industry sector', label: 'Industry sector'},
                    {value: 'Line of Business', label: 'Line of Business'},
                    {
                      value: 'Products and services',
                      label: 'Products and services'
                    },
                    {value: 'Transaction size', label: 'Transaction size'},
                    {
                      value: 'Segment of the value chain',
                      label: 'Segment of the value chain'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_10_VALUE,
                    condition: conditionType.GREATER_THAN,
                    numericValue: 100
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_12_VALUE,
                  fieldLabel:
                    'Explain how criteria coverage and/or exceptions have been determined',
                  colSpan: true,
                  textLimit: 2500
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_13_VALUE,
                  fieldLabel: 'Requirements for clients/investees',
                  options: [
                    {
                      value: 'Commitment to a circular economy strategy',
                      label: 'Commitment to a circular economy strategy'
                    },
                    {
                      value:
                        'Commitment to avoidance of negative impacts on threatened and protected species [appears only if B, F, or W is selected in C1]',
                      label:
                        'Commitment to avoidance of negative impacts on threatened and protected species [appears only if B, F, or W is selected in C1]'
                    },
                    {
                      value:
                        'Commitment to comply with regulations and mandatory standards',
                      label:
                        'Commitment to comply with regulations and mandatory standards'
                    },
                    {
                      value:
                        'Commitment to take environmental action beyond regulatory compliance',
                      label:
                        'Commitment to take environmental action beyond regulatory compliance'
                    },
                    {
                      value:
                        'Commitment to engage in integrated, multi-stakeholder landscape (including river basin) initiatives to promote shared sustainability goals [appears only if F or W is selected in C1]',
                      label:
                        'Commitment to engage in integrated, multi-stakeholder landscape (including river basin) initiatives to promote shared sustainability goals [appears only if F or W is selected in C1]'
                    },
                    {
                      value:
                        'Commitment to implementation of nature-based solutions that support landscape restoration and long-term protection of natural ecosystems',
                      label:
                        'Commitment to implementation of nature-based solutions that support landscape restoration and long-term protection of natural ecosystems'
                    },
                    {
                      value:
                        'Commitment to Net Positive Gain [appears only if B, F, or W is selected in C1]',
                      label:
                        'Commitment to Net Positive Gain [appears only if B, F, or W is selected in C1]'
                    },
                    {
                      value:
                        'Commitment to No Net Loss [appears only if B, F, or W is selected in C1]',
                      label:
                        'Commitment to No Net Loss [appears only if B, F, or W is selected in C1]'
                    },
                    {
                      value:
                        'Commitment to no trade of CITES listed species [appears only if B or F is selected in C1]',
                      label:
                        'Commitment to no trade of CITES listed species [appears only if B or F is selected in C1]'
                    },
                    {
                      value:
                        'Commitment to respect legally designated protected areas [appears only if B, F, or W is selected in C1]',
                      label:
                        'Commitment to respect legally designated protected areas [appears only if B, F, or W is selected in C1]'
                    },
                    {
                      value:
                        'Commitment to stakeholder engagement and capacity building on environmental issues',
                      label:
                        'Commitment to stakeholder engagement and capacity building on environmental issues'
                    },
                    {
                      value: 'Other environmental commitment, please specify',
                      label: 'Other environmental commitment, please specify'
                    },
                    {
                      value: 'Commitment to 100% renewable energy',
                      label: 'Commitment to 100% renewable energy'
                    },
                    {
                      value: 'Commitment to net-zero emissions',
                      label: 'Commitment to net-zero emissions'
                    },
                    {
                      value:
                        'Commitment to not invest in fossil-fuel expansion',
                      label: 'Commitment to not invest in fossil-fuel expansion'
                    },
                    {
                      value: 'Commitment to zero flaring [CC-OG/CO only]',
                      label: 'Commitment to zero flaring [CC-OG/CO only]'
                    },
                    {
                      value:
                        'Commitment to not funding climate-denial or lobbying against climate regulations',
                      label:
                        'Commitment to not funding climate-denial or lobbying against climate regulations'
                    },
                    {
                      value: 'Other climate-related commitment, please specify',
                      label: 'Other climate-related commitment, please specify'
                    },
                    {
                      value:
                        'Commitment to best management practices for soils and peat',
                      label:
                        'Commitment to best management practices for soils and peat'
                    },
                    {
                      value:
                        'Commitment to conduct or support restoration and/or compensation to remedy for past deforestation or conversion',
                      label:
                        'Commitment to conduct or support restoration and/or compensation to remedy for past deforestation or conversion'
                    },
                    {
                      value:
                        'Commitment to facilitate the inclusion of smallholders into the value chain',
                      label:
                        'Commitment to facilitate the inclusion of smallholders into the value chain'
                    },
                    {
                      value:
                        'Commitment to no deforestation, to no planting on peatlands, and to no exploitation (NDPE) by target date, please specify',
                      label:
                        'Commitment to no deforestation, to no planting on peatlands, and to no exploitation (NDPE) by target date, please specify'
                    },
                    {
                      value:
                        'Commitment to no development on peat regardless of depth',
                      label:
                        'Commitment to no development on peat regardless of depth'
                    },
                    {
                      value:
                        'Commitment to no land clearance by burning or clear-cutting',
                      label:
                        'Commitment to no land clearance by burning or clear-cutting'
                    },
                    {
                      value:
                        'Commitment to no-conversion of natural ecosystems by target date, please specify',
                      label:
                        'Commitment to no-conversion of natural ecosystems by target date, please specify'
                    },
                    {
                      value:
                        'Commitment to no-deforestation by target date, please specify',
                      label:
                        'Commitment to no-deforestation by target date, please specify'
                    },
                    {
                      value:
                        'Commitment to the use of the High Conservation Value (HCV) approach',
                      label:
                        'Commitment to the use of the High Conservation Value (HCV) approach'
                    },
                    {
                      value: 'Other forests-related commitment, please specify',
                      label: 'Other forests-related commitment, please specify'
                    },
                    {
                      value:
                        'Commitment to reduce or phase out hazardous substances',
                      label:
                        'Commitment to reduce or phase out hazardous substances'
                    },
                    {
                      value:
                        'Commitment to control/reduce/eliminate water pollution',
                      label:
                        'Commitment to control/reduce/eliminate water pollution'
                    },
                    {
                      value: 'Commitment to reduce water consumption volumes',
                      label: 'Commitment to reduce water consumption volumes'
                    },
                    {
                      value: 'Commitment to reduce water withdrawal volumes',
                      label: 'Commitment to reduce water withdrawal volumes'
                    },
                    {
                      value:
                        'Commitment to safely managed WASH in local communities',
                      label:
                        'Commitment to safely managed WASH in local communities'
                    },
                    {
                      value:
                        'Commitment to the conservation of freshwater ecosystems',
                      label:
                        'Commitment to the conservation of freshwater ecosystems'
                    },
                    {
                      value:
                        'Commitment to water stewardship and/or collective action',
                      label:
                        'Commitment to water stewardship and/or collective action'
                    },
                    {
                      value: 'Other water-related commitment, please specify',
                      label: 'Other water-related commitment, please specify'
                    },
                    {
                      value:
                        'Adoption of the UN International Labour Organization principles',
                      label:
                        'Adoption of the UN International Labour Organization principles'
                    },
                    {
                      value:
                        'Commitment to promote gender equality and women’s empowerment',
                      label:
                        'Commitment to promote gender equality and women’s empowerment'
                    },
                    {
                      value:
                        'Commitment to respect and protect the customary rights to land, resources, and territory of Indigenous Peoples and Local Communities',
                      label:
                        'Commitment to respect and protect the customary rights to land, resources, and territory of Indigenous Peoples and Local Communities'
                    },
                    {
                      value:
                        'Commitment to respect internationally recognized human rights',
                      label:
                        'Commitment to respect internationally recognized human rights'
                    },
                    {
                      value:
                        'Commitment to secure Free, Prior, and Informed Consent (FPIC) of Indigenous Peoples and local communities',
                      label:
                        'Commitment to secure Free, Prior, and Informed Consent (FPIC) of Indigenous Peoples and local communities'
                    },
                    {
                      value: 'Other social commitment, please specify',
                      label: 'Other social commitment, please specify'
                    },
                    {
                      value:
                        'Acknowledgement of the human right to water and sanitation [appears if “Water” is selected]',
                      label:
                        'Acknowledgement of the human right to water and sanitation [appears if “Water” is selected]'
                    },
                    {
                      value:
                        'Description of biodiversity-related performance standards [appears if “Biodiversity” is selected]',
                      label:
                        'Description of biodiversity-related performance standards [appears if “Biodiversity” is selected]'
                    },
                    {
                      value:
                        'Description of commodities covered by the policy [appears if “Forests” is selected]',
                      label:
                        'Description of commodities covered by the policy [appears if “Forests” is selected]'
                    },
                    {
                      value:
                        'Description of dependencies on natural resources and ecosystems',
                      label:
                        'Description of dependencies on natural resources and ecosystems'
                    },
                    {
                      value:
                        'Description of impacts on natural resources and ecosystems',
                      label:
                        'Description of impacts on natural resources and ecosystems'
                    },
                    {
                      value:
                        'Description of environmental requirements for procurement',
                      label:
                        'Description of environmental requirements for procurement'
                    },
                    {
                      value:
                        'Description of grievance/whistleblower mechanism to monitor non-compliance with the environmental policy and raise/address/escalate any other greenwashing concerns',
                      label:
                        'Description of grievance/whistleblower mechanism to monitor non-compliance with the environmental policy and raise/address/escalate any other greenwashing concerns'
                    },
                    {
                      value:
                        'Description of membership and financial support provided to organizations that seek to influence public policy',
                      label:
                        'Description of membership and financial support provided to organizations that seek to influence public policy'
                    },
                    {
                      value:
                        'Description of renewable electricity procurement practices [appears if “Climate change” is selected]',
                      label:
                        'Description of renewable electricity procurement practices [appears if “Climate change” is selected]'
                    },
                    {
                      value:
                        'Recognition of environmental linkages and trade-offs',
                      label:
                        'Recognition of environmental linkages and trade-offs'
                    },
                    {
                      value:
                        'Reference to timebound environmental milestones and targets',
                      label:
                        'Reference to timebound environmental milestones and targets'
                    },
                    {
                      value:
                        'Other additional reference/description, please specify',
                      label:
                        'Other additional reference/description, please specify'
                    }
                  ],

                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_14_VALUE,
                  fieldLabel:
                    'Measurement of proportion of clients/investees compliant with the policy',
                  options: PROCESS_PLACE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_15_VALUE,
                  fieldLabel:
                    '% of clients/investees compliant with the policy',
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_16_VALUE,
                  fieldLabel:
                    '% of portfolio value that is compliant with the policy',
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_17_VALUE,
                  fieldLabel: 'Target year for 100% compliance',
                  options: [
                    {value: 'Already met', label: 'Already met'},
                    {
                      value: 'Within the next year',
                      label: 'Within the next year'
                    },
                    {
                      value: 'Within the next 2 years',
                      label: 'Within the next 2 years'
                    },
                    {
                      value: 'Within the next 5 years',
                      label: 'Within the next 5 years'
                    },
                    {
                      value: 'In more than 5 years',
                      label: 'In more than 5 years'
                    },
                    {value: 'No timeframe', label: 'No timeframe'},
                    {
                      value: 'We have not set a target for 100% compliance',
                      label: 'We have not set a target for 100% compliance'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q13_18_VALUE,
                  fieldLabel:
                    'Explain why your organization does not measure the % of clients/investees compliant with the policy ',
                  colSpan: true,
                  textLimit: 2500
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.7.2 Provide details of your exclusion policies related to industries, activities and/or locations exposed or contributing to environmental risks.',
            tooltip:
              'Exclusion policies are an element of financial sector companies’ negative screening processes to reduce portfolio exposure to environmentally vulnerable projects and/or investments, and to implement environmental commitments. Data users are interested in understanding the types of environmental policy exclusions and the effect that these exclusions have had or will have on the organizations’ exposure.',
            conditionalVisibility: {
              dependsOn:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q12_VALUE,
              showWhen: [
                'Yes, we have exclusion policies for industries, activities and/or locations exposed or contributing to environmental risks',
                'Yes, our framework includes both policies with environmental client/investee requirements and environmental exclusion policies'
              ],
              condition: conditionType.EQUALS,
              fieldName:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q12_2_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q14_VALUE,
            dependsOn:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q12_2_VALUE,
            showWhen: [
              'Yes, we have exclusion policies for industries, activities and/or locations exposed or contributing to environmental risks',
              'Yes, our framework includes both policies with environmental client/investee requirements and environmental exclusion policies'
            ],
            relatedTableKey:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q12_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q14_1_VALUE,
                  fieldLabel: 'Portfolio',
                  options: [
                    {value: 'Banking (Bank)', label: 'Banking (Bank)'},
                    {
                      value: 'Investing (Asset manager)',
                      label: 'Investing (Asset manager)'
                    },
                    {
                      value: 'Investing (Asset owner)',
                      label: 'Investing (Asset owner)'
                    },
                    {
                      value: 'Insurance (Insurance company)',
                      label: 'Insurance (Insurance company)'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q14_2_VALUE,
                  fieldLabel: 'Type of exclusion policy',
                  options: [
                    {
                      value: 'All fossil fuels [CC only]',
                      label: 'All fossil fuels [CC only]'
                    },
                    {value: 'All coal [CC only]', label: 'All coal [CC only]'},
                    {
                      value: 'Thermal coal [CC only]',
                      label: 'Thermal coal [CC only]'
                    },
                    {
                      value: 'Fuel from liquified coal [CC only]',
                      label: 'Fuel from liquified coal [CC only]'
                    },
                    {
                      value: 'Coal mining [CC only]',
                      label: 'Coal mining [CC only]'
                    },
                    {
                      value: 'Mountaintop removal mining [CC only]',
                      label: 'Mountaintop removal mining [CC only]'
                    },
                    {
                      value: 'Power from coal [CC only]',
                      label: 'Power from coal [CC only]'
                    },
                    {
                      value: 'All oil & gas [CC only]',
                      label: 'All oil & gas [CC only]'
                    },
                    {
                      value: 'Oil from tar sands [CC only]',
                      label: 'Oil from tar sands [CC only]'
                    },
                    {
                      value: 'Oil from shale [CC only]',
                      label: 'Oil from shale [CC only]'
                    },
                    {
                      value: 'Gas from shale [CC only]',
                      label: 'Gas from shale [CC only]'
                    },
                    {
                      value: 'Arctic oil and gas [CC only]',
                      label: 'Arctic oil and gas [CC only]'
                    },
                    {
                      value: 'Ultra-deepwater oil and gas [CC only]',
                      label: 'Ultra-deepwater oil and gas [CC only]'
                    },
                    {
                      value: 'Fracked oil and gas [CC only]',
                      label: 'Fracked oil and gas [CC only]'
                    },
                    {
                      value: 'Liquified natural gas [CC only]',
                      label: 'Liquified natural gas [CC only]'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q14_3_VALUE,
                  fieldLabel: 'Fossil fuel value chain',
                  options: [
                    {value: 'Upstream', label: 'Upstream'},
                    {value: 'Midstream', label: 'Midstream'},
                    {value: 'Downstream', label: 'Downstream'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q14_4_VALUE,
                  fieldLabel: 'Year of exclusion implementation',
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q14_5_VALUE,
                  fieldLabel: 'Phaseout pathway',
                  options: [
                    {
                      value: 'New business/investment for new projects',
                      label: 'New business/investment for new projects'
                    },
                    {
                      value: 'New business/investment for existing projects',
                      label: 'New business/investment for existing projects'
                    },
                    {
                      value:
                        'Existing business/investment for existing projects',
                      label:
                        'Existing business/investment for existing projects'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q14_6_VALUE,
                  fieldLabel: 'Year of complete phaseout',
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q14_7_VALUE,
                  fieldLabel: 'Country/area the exclusion policy applies to',
                  options: [
                    {value: 'Worldwide', label: 'Worldwide'},
                    {
                      value: '[List of forest countries/areas]',
                      label: '[List of forest countries/areas]'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q14_8_VALUE,
                  fieldLabel: 'Description',
                  colSpan: true,
                  textLimit: 2500
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.8 Does your organization include covenants in financing agreements to reflect and enforce your environmental policies?',
            tooltip: `Banks can influence their clients through requirements within the terms of financing
                     agreements and with the option of a default being triggered should there be a failure to comply. Data users are interested in whether banks are using this method to promote action on environmental performance because there are potential benefits for the bank including a perceived improvement in credit profile and access to alternative pools of capital.`,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q15_VALUE
            // conditionalVisibility: {
            //     dependsOn:
            //     CDP_CORPORATE_INTRO_FIELD_NAMES.CORPORATE_INTRO_Q12_VALUE,
            //     showWhen: ['Yes'],
            //     condition: conditionType.EQUALS,
            //     fieldName:
            //     CDP_CORPORATE_INTRO_FIELD_NAMES.CORPORATE_INTRO_Q12_2_VALUE_1
            //   }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q15_VALUE,
            // dependsOn:  CDP_CORPORATE_INTRO_FIELD_NAMES.CORPORATE_INTRO_Q12_2_VALUE_1,
            // showWhen: ['Yes'],
            // relatedTableKey:  CDP_CORPORATE_INTRO_FIELD_NAMES.CORPORATE_INTRO_Q12_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q15_1_VALUE,
                  fieldLabel:
                    'Covenants included in financing agreements to reflect and enforce policies ',
                  options: PROCESS_PLACE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q15_2_VALUE,
                  fieldLabel:
                    'Primary reason for not including covenants in financing agreements',
                  options:
                    PRIMARY_REASONS_FOR_NOT_IMPLEMENTING_ENVIRONMENTAL_INITIATIVES_OPTIONS,
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q15_1_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'No, but we plan to within the next two years',
                      'No, and we do not plan to within the next two years'
                    ]
                  },
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q15_1_VALUE,
                    condition: conditionType.EQUALS,
                    values: [
                      'No, but we plan to within the next two years',
                      'No, and we do not plan to within the next two years'
                    ],
                    required: true,
                    dependentValue:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q15_1_VALUE
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q15_3_VALUE,
                  fieldLabel:
                    'Explain why your organization does not include covenants in financing agreements ',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q15_1_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'No, but we plan to within the next two years',
                      'No, and we do not plan to within the next two years'
                    ]
                  },
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q15_1_VALUE,
                    condition: conditionType.EQUALS,
                    values: [
                      'No, but we plan to within the next two years',
                      'No, and we do not plan to within the next two years'
                    ],
                    required: true,
                    dependentValue:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q15_1_VALUE
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.8.1 Provide details of the covenants included in your organization’s financing agreements to reflect and enforce your environmental policies.',
            tooltip:
              'Banks can influence their clients through requirements within the terms of financing agreements and with the option of a default being triggered should there be a failure to comply. Data users are interested in whether banks are using this to enforce their environmental policies and promote action on environmental performance, including a perceived improvement in credit profile and access to alternative pools of capital.',
            conditionalVisibility: {
              dependsOn:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q15_VALUE,
              showWhen: ['Yes'],
              condition: conditionType.EQUALS,
              fieldName:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q15_1_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q16_VALUE,
            dependsOn:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q15_1_VALUE,
            showWhen: ['Yes'],
            relatedTableKey:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q15_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q16_1_VALUE,
                  fieldLabel: 'Environmental issue',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Water', label: 'Water'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Biodiversity', label: 'Biodiversity'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q16_2_VALUE,
                  fieldLabel: 'Types of covenants used',
                  options: [
                    {
                      value:
                        'A purpose or use of proceeds clause that refers to a taxonomy-aligned activity',
                      label:
                        'A purpose or use of proceeds clause that refers to a taxonomy-aligned activity'
                    },
                    {
                      value:
                        'Margin or pricing depends on sustainability criteria',
                      label:
                        'Margin or pricing depends on sustainability criteria'
                    },
                    {
                      value:
                        'Minimum level of taxonomy-aligned assets are mandated',
                      label:
                        'Minimum level of taxonomy-aligned assets are mandated'
                    },
                    {
                      value:
                        'Legal mandate to obtain third-party verification of sustainability criteria',
                      label:
                        'Legal mandate to obtain third-party verification of sustainability criteria'
                    },
                    {
                      value:
                        'Covenants related to compliance with your environmental policies',
                      label:
                        'Covenants related to compliance with your environmental policies'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q16_3_VALUE,
                  fieldLabel: 'Asset class/product types covered by covenants',
                  options: [
                    {value: 'Corporate loans', label: 'Corporate loans'},
                    {value: 'Retail loans', label: 'Retail loans'},
                    {
                      value: 'Corporate real estate',
                      label: 'Corporate real estate'
                    },
                    {value: 'Retail mortgages', label: 'Retail mortgages'},
                    {value: 'Trade finance', label: 'Trade finance'},
                    {value: 'Asset finance', label: 'Asset finance'},
                    {value: 'Project finance', label: 'Project finance'},
                    {
                      value: 'Debt and equity underwriting',
                      label: 'Debt and equity underwriting'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q16_4_VALUE,
                  fieldLabel: 'Criteria for how covenants are applied',
                  options: [
                    {
                      value: 'All business/investment for all projects',
                      label: 'All business/investment for all projects'
                    },
                    {
                      value: 'New business/investment for all projects',
                      label: 'New business/investment for all projects'
                    },
                    {
                      value: 'New business/investment for new projects',
                      label: 'New business/investment for new projects'
                    },
                    {value: 'Selected clients', label: 'Selected clients'},
                    {
                      value: 'Depending on loan size',
                      label: 'Depending on loan size'
                    },
                    {
                      value: 'Depending on loan tenor',
                      label: 'Depending on loan tenor'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q16_5_VALUE,
                  fieldLabel: '% of clients covered by covenants',
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q16_6_VALUE,
                  fieldLabel:
                    '% of portfolio covered in relation to total portfolio value',
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q16_7_VALUE,
                  fieldLabel:
                    'Provide details on which environmental policies your covenants enforce, and how',
                  colSpan: true
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.9 Does your organization offer its employees a pension scheme that incorporates environmental criteria in its holdings?',
            tooltip:
              'Consideration of environmental factors in pension scheme holdings contributes to the financing of a sustainable economy and demonstrates that organizations consider such risks and opportunities in their assessment of pension plan options. This question allows data users to understand how the organization is contributing to sustainable investing. '
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q17_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q17_1_VALUE,
                  fieldLabel: 'Environmental issue',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Water', label: 'Water'},
                    {value: 'Forests', label: 'Forests'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q17_2_VALUE,
                  fieldLabel:
                    'Pension scheme incorporates environmental criteria in its holdings',
                  options: [
                    {
                      value:
                        'Yes, as the default investment strategy for all plans',
                      label:
                        'Yes, as the default investment strategy for all plans'
                    },
                    {
                      value: 'Yes, as an investment option',
                      label: 'Yes, as an investment option'
                    },
                    {
                      value:
                        'No, but we plan to incorporate in the next two years',
                      label:
                        'No, but we plan to incorporate in the next two years'
                    },
                    {
                      value:
                        'No, and we do not plan to incorporate in the next two years',
                      label:
                        'No, and we do not plan to incorporate in the next two years'
                    },
                    {
                      value:
                        'No, due to a compulsory national/subnational scheme',
                      label:
                        'No, due to a compulsory national/subnational scheme'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q17_3_VALUE,
                  fieldLabel:
                    'Describe how funds within the pension scheme are selected and how your organization ensures that environmental criteria are incorporated',
                  colSpan: true,
                  textLimit: 5000,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q17_2_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'Yes, as the default investment strategy for all plans',
                      'Yes, as an investment option'
                    ]
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q17_4_VALUE,
                  fieldLabel:
                    'Explain why your organization does not incorporate criteria for this environmental issue into the pension scheme holdings ',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q17_2_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'No, but we plan to incorporate in the next two years',
                      'No, and we do not plan to incorporate in the next two years',
                      'No, due to a compulsory national/subnational scheme'
                    ]
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.10 Are you a signatory or member of any environmental collaborative frameworks or initiatives?',
            tooltip:
              'By becoming a signatory of environmental collaborative industry initiatives, organizations contribute to the development of environmental disclosure frameworks, metrics, and goals that will help transition into a more sustainable economy. Supporting environmental industry initiatives sends a signal to investors about the organization’s commitment to taking steps to align its business with this transition. Endorsing widely known public initiatives reflects good practice and meets data users’ demand for environmental action, as organizations have the opportunity to benchmark themselves against their peers.'
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q18_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q18_1_VALUE,
                  fieldLabel:
                    'Are you a signatory or member of any environmental collaborative frameworks or initiatives?',
                  options: PROCESS_PLACE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q18_2_VALUE,
                  fieldLabel: 'Collaborative framework or initiative',
                  options: DESCRIBE_YOUR_ORGANIZATION_OPTIONS,
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q18_1_VALUE,
                    condition: conditionType.NOT,
                    values: ['Yes']
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q18_3_VALUE,
                  fieldLabel:
                    'Describe your organization’s role within each framework or initiative',
                  colSpan: true,
                  textLimit: 5000,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q18_1_VALUE,
                    condition: conditionType.NOT,
                    values: ['Yes']
                  }
                }
              ]
            }
          }
        ]
      },

      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.11 In the reporting year, did your organization engage in activities that could directly or indirectly influence policy, law, or regulation that may (positively or negatively) impact the environment?',
            tooltip:
              'Data users wish to understand how an organization’s policy engagement on environmental issues relates to other stances taken. It is important that organizations maintain a consistent approach to environmental issues – for instance, engaging in activities whose purpose is to discredit environmental science, while also collaborating with other groups to advance solutions for environmental issues sends conflicting messages to data users about that organization’s priorities and stance. This question provides data users with insights into the different external engagement activities of organizations and enables organizations to disclose the processes they use to ensure that their position on environmental issues is compatible with the positive contributions in which they partake, as well as with global environmental treaties or goals. '
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_1_VALUE,
                  fieldLabel:
                    'External engagement activities that could directly or indirectly influence policy, law, or regulation that may impact the environment',
                  options: [
                    {
                      value: 'Yes, we engaged directly with policy makers',
                      label: 'Yes, we engaged directly with policy makers'
                    },
                    {
                      value:
                        'Yes, we engaged indirectly through, and/or provided financial or in-kind support to a trade association or other intermediary organization or individual whose activities could influence policy, law, or regulation',
                      label:
                        'Yes, we engaged indirectly through, and/or provided financial or in-kind support to a trade association or other intermediary organization or individual whose activities could influence policy, law, or regulation'
                    },
                    {
                      value:
                        'No, we have assessed our activities, and none could directly or indirectly influence policy, law, or regulation that may impact the environment',
                      label:
                        'No, we have assessed our activities, and none could directly or indirectly influence policy, law, or regulation that may impact the environment'
                    },
                    {value: 'Not assessed', label: 'Not assessed'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_2_VALUE,
                  fieldLabel:
                    'Indicate whether your organization has a public commitment or position statement to conduct your engagement activities in line with global environmental treaties or policy goals',
                  options: [
                    {
                      value:
                        'Yes, we have a public commitment or position statement in line with global environmental treaties or policy goals',
                      label:
                        'Yes, we have a public commitment or position statement in line with global environmental treaties or policy goals'
                    },
                    {
                      value:
                        'No, but we plan to have one in the next two years',
                      label: 'No, but we plan to have one in the next two years'
                    },
                    {
                      value:
                        'No, and we do not plan to have one in the next two years',
                      label:
                        'No, and we do not plan to have one in the next two years'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_1_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: [
                      'Yes, we engaged directly with policy makers',
                      'Yes, we engaged indirectly through, and/or provided financial or in-kind support to a trade association or other intermediary organization or individual whose activities could influence policy, law, or regulation'
                    ]
                  }
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_3_VALUE,
                  fieldLabel:
                    'Global environmental treaties or policy goals in line with public commitment or position statement',
                  options: [
                    {
                      value: 'Paris Agreement [CC only]',
                      label: 'Paris Agreement [CC only]'
                    },
                    {
                      value:
                        'Kunming-Montreal Global Biodiversity Framework [W and F only]',
                      label:
                        'Kunming-Montreal Global Biodiversity Framework [W and F only]'
                    },
                    {
                      value:
                        'Sustainable Development Goal 6 on Clean Water and Sanitation [W only]',
                      label:
                        'Sustainable Development Goal 6 on Clean Water and Sanitation [W only]'
                    },
                    {
                      value:
                        'Another global environmental treaty or policy goal, please specify',
                      label:
                        'Another global environmental treaty or policy goal, please specify'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_1_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: [
                      'Yes, we engaged directly with policy makers',
                      'Yes, we engaged indirectly through, and/or provided financial or in-kind support to a trade association or other intermediary organization or individual whose activities could influence policy, law, or regulation'
                    ]
                  }
                },
                {
                  type: inputType.FILEUPLOAD,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_4_VALUE,
                  fieldLabel: 'Attach commitment or position statement',
                  colSpan: true,
                  fileUploadAction:
                    API_ENDPOINT_JSON.ESG.CDP_CORPORATE.documentUpload
                      .governance,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_1_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: [
                      'Yes, we engaged directly with policy makers',
                      'Yes, we engaged indirectly through, and/or provided financial or in-kind support to a trade association or other intermediary organization or individual whose activities could influence policy, law, or regulation'
                    ]
                  }
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_5_VALUE,
                  fieldLabel:
                    'Indicate whether your organization is registered on a transparency register',
                  options: [
                    {value: 'Yes', label: 'Yes'},
                    {value: 'No', label: 'No'},
                    {value: 'Unknown', label: 'Unknown'}
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_1_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: [
                      'Yes, we engaged directly with policy makers',
                      'Yes, we engaged indirectly through, and/or provided financial or in-kind support to a trade association or other intermediary organization or individual whose activities could influence policy, law, or regulation'
                    ]
                  }
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_6_VALUE,
                  fieldLabel:
                    'Types of transparency register your organization is registered on',
                  options: [
                    {
                      value: 'Mandatory government register',
                      label: 'Mandatory government register'
                    },
                    {
                      value: 'Voluntary government register',
                      label: 'Voluntary government register'
                    },
                    {
                      value: 'Non-government register',
                      label: 'Non-government register'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_1_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: [
                      'Yes, we engaged directly with policy makers',
                      'Yes, we engaged indirectly through, and/or provided financial or in-kind support to a trade association or other intermediary organization or individual whose activities could influence policy, law, or regulation'
                    ]
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_7_VALUE,
                  fieldLabel:
                    'Disclose the transparency registers on which your organization is registered & the relevant ID numbers for your organization',
                  colSpan: true,
                  textLimit: 500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_1_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: [
                      'Yes, we engaged directly with policy makers',
                      'Yes, we engaged indirectly through, and/or provided financial or in-kind support to a trade association or other intermediary organization or individual whose activities could influence policy, law, or regulation'
                    ]
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_8_VALUE,
                  fieldLabel:
                    'Describe the process your organization has in place to ensure that your external engagement activities are consistent with your environmental commitments and/or transition plan',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_1_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: [
                      'Yes, we engaged directly with policy makers',
                      'Yes, we engaged indirectly through, and/or provided financial or in-kind support to a trade association or other intermediary organization or individual whose activities could influence policy, law, or regulation'
                    ]
                  }
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_9_VALUE,
                  fieldLabel:
                    'Primary reason for not engaging in activities that could directly or indirectly influence policy, law, or regulation that may impact the environment',
                  options: [
                    {
                      value: 'Contractual hindrances',
                      label: 'Contractual hindrances'
                    },
                    {
                      value:
                        'Lack of internal resources, capabilities, or expertise (e.g., due to organization size)',
                      label:
                        'Lack of internal resources, capabilities, or expertise (e.g., due to organization size)'
                    },
                    {
                      value: 'No standardized procedure',
                      label: 'No standardized procedure'
                    },
                    {
                      value: 'Not an immediate strategic priority',
                      label: 'Not an immediate strategic priority'
                    },
                    {
                      value: 'Judged to be unimportant or not relevant',
                      label: 'Judged to be unimportant or not relevant'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_1_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: [
                      'No, we have assessed our activities, and none could directly or indirectly influence policy, law, or regulation that may impact the environment',
                      'Not assessed'
                    ]
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_10_VALUE,
                  fieldLabel:
                    'Explain why your organization does not engage in activities that could directly or indirectly influence policy, law, or regulation that may impact the environment',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_1_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: [
                      'No, we have assessed our activities, and none could directly or indirectly influence policy, law, or regulation that may impact the environment',
                      'Not assessed'
                    ]
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.11.1 On what policies, laws, or regulations that may (positively or negatively) impact the environment has your organization been engaging directly with policy makers in the reporting year?',
            tooltip:
              'Data users wish to understand how an organization’s policy engagement on environmental issues relate to other stances taken. This question provides increased transparency regarding an organization’s direct engagement with policy makers, and whether the engagement is aligned with global environmental treaties or goals.',
            conditionalVisibility: {
              dependsOn:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_VALUE,
              showWhen: ['Yes, we engaged directly with policy makers'],
              condition: conditionType.EQUALS,
              fieldName:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_1_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q20_VALUE,
            dependsOn:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_1_VALUE,
            showWhen: ['Yes, we engaged directly with policy makers'],
            relatedTableKey:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q20_1_VALUE,
                  fieldLabel:
                    'Specify the policy, law, or regulation on which your organization is engaging with policy makers',
                  colSpan: true,
                  textLimit: 500
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q20_2_VALUE,
                  fieldLabel:
                    'Environmental issues the policy, law, or regulation relates to',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Water', label: 'Water'},
                    {value: 'Forests', label: 'Forests'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q20_3_VALUE,
                  fieldLabel:
                    'Focus area of policy, law, or regulation that may impact the environment',
                  options: [
                    {
                      value: 'Emissions – CO2 [CC only]',
                      label: 'Emissions – CO2 [CC only]'
                    },
                    {
                      value: 'Emissions – methane [CC only]',
                      label: 'Emissions – methane [CC only]'
                    },
                    {
                      value: 'Emissions – other GHGs [CC only]',
                      label: 'Emissions – other GHGs [CC only]'
                    },
                    {
                      value: 'Forest fires [F only]',
                      label: 'Forest fires [F only]'
                    },
                    {
                      value: 'Hazardous substances',
                      label: 'Hazardous substances'
                    },
                    {
                      value:
                        'Use of pesticides and agrochemicals [W and F only]',
                      label:
                        'Use of pesticides and agrochemicals [W and F only]'
                    },
                    {
                      value: 'Water availability [W only]',
                      label: 'Water availability [W only]'
                    },
                    {
                      value: 'Water pollution [W only]',
                      label: 'Water pollution [W only]'
                    },
                    {
                      value:
                        'Other environmental impacts and pressures, please specify',
                      label:
                        'Other environmental impacts and pressures, please specify'
                    },
                    {
                      value: 'Alternative fuels [CC only]',
                      label: 'Alternative fuels [CC only]'
                    },
                    {
                      value: 'Electricity grid access for renewables [CC only]',
                      label: 'Electricity grid access for renewables [CC only]'
                    },
                    {
                      value: 'Energy attribute certificate systems [CC only]',
                      label: 'Energy attribute certificate systems [CC only]'
                    },
                    {
                      value: 'Energy efficiency requirements [CC only]',
                      label: 'Energy efficiency requirements [CC only]'
                    },
                    {
                      value:
                        'Green electricity tariffs/renewable energy PPAs [CC only]',
                      label:
                        'Green electricity tariffs/renewable energy PPAs [CC only]'
                    },
                    {
                      value:
                        'Low-carbon, non-renewable energy generation [CC only]',
                      label:
                        'Low-carbon, non-renewable energy generation [CC only]'
                    },
                    {
                      value: 'Minimum energy efficiency requirements [CC only]',
                      label: 'Minimum energy efficiency requirements [CC only]'
                    },
                    {
                      value:
                        'New fossil fuel energy generation capacity [CC only]',
                      label:
                        'New fossil fuel energy generation capacity [CC only]'
                    },
                    {
                      value: 'Renewable energy generation [CC only]',
                      label: 'Renewable energy generation [CC only]'
                    },
                    {
                      value: 'Other energy and renewables, please specify',
                      label: 'Other energy and renewables, please specify'
                    },
                    {
                      value: 'Environmental registries',
                      label: 'Environmental registries'
                    },
                    {
                      value:
                        'Environmental protection requirements [W and F only]',
                      label:
                        'Environmental protection requirements [W and F only]'
                    },
                    {
                      value:
                        'Financing & incentivizing sustainable forest management [F only]',
                      label:
                        'Financing & incentivizing sustainable forest management [F only]'
                    },
                    {
                      value: 'Forest management plans [F only]',
                      label: 'Forest management plans [F only]'
                    },
                    {
                      value: 'Forest private reserves [F only]',
                      label: 'Forest private reserves [F only]'
                    },
                    {
                      value: 'Land Conservation and Protected Areas [F only]',
                      label: 'Land Conservation and Protected Areas [F only]'
                    },
                    {
                      value:
                        'Landscape (including river basin) and jurisdictional approaches [W and F only]',
                      label:
                        'Landscape (including river basin) and jurisdictional approaches [W and F only]'
                    },
                    {
                      value: 'Legal reserves [F only]',
                      label: 'Legal reserves [F only]'
                    },
                    {
                      value: 'Operations permits [W and F only]',
                      label: 'Operations permits [W and F only]'
                    },
                    {
                      value: 'Peatland management [F only]',
                      label: 'Peatland management [F only]'
                    },
                    {
                      value: 'Restoration/rehabilitation [W and F only]',
                      label: 'Restoration/rehabilitation [W and F only]'
                    },
                    {
                      value:
                        'Resilience and adaptive capacity of forests [F only]',
                      label:
                        'Resilience and adaptive capacity of forests [F only]'
                    },
                    {
                      value: 'Socio-economic land-use planning [W and F only]',
                      label: 'Socio-economic land-use planning [W and F only]'
                    },
                    {
                      value: 'Transboundary water management [W only]',
                      label: 'Transboundary water management [W only]'
                    },
                    {
                      value:
                        'Other environmental protection and management procedures, please specify',
                      label:
                        'Other environmental protection and management procedures, please specify'
                    },
                    {
                      value:
                        'Agricultural subsidies for growing water intensive crops in water-stressed areas [W only]',
                      label:
                        'Agricultural subsidies for growing water intensive crops in water-stressed areas [W only]'
                    },
                    {
                      value:
                        'Agricultural subsidies for intensive production that impacts soil health [F only]',
                      label:
                        'Agricultural subsidies for intensive production that impacts soil health [F only]'
                    },
                    {
                      value:
                        'Agricultural subsidies for intensive production that may lead to water pollution (e.g., fertilizer runoff) [W only]',
                      label:
                        'Agricultural subsidies for intensive production that may lead to water pollution (e.g., fertilizer runoff) [W only]'
                    },
                    {
                      value:
                        'Agricultural subsidies for production linked with deforestation or conversion of other natural ecosystems [F only]',
                      label:
                        'Agricultural subsidies for production linked with deforestation or conversion of other natural ecosystems [F only]'
                    },
                    {
                      value:
                        'Agricultural subsidies for production with high emission intensity [CC only]',
                      label:
                        'Agricultural subsidies for production with high emission intensity [CC only]'
                    },
                    {
                      value: 'Carbon offsets [CC and F only]',
                      label: 'Carbon offsets [CC and F only]'
                    },
                    {
                      value: 'Carbon taxes [CC only]',
                      label: 'Carbon taxes [CC only]'
                    },
                    {
                      value: 'Emissions trading schemes [CC only]',
                      label: 'Emissions trading schemes [CC only]'
                    },
                    {
                      value:
                        'Fines, enforcement orders and/or penalties relating to groundwater contamination [W only]',
                      label:
                        'Fines, enforcement orders and/or penalties relating to groundwater contamination [W only]'
                    },
                    {
                      value:
                        'Fines, enforcement orders and/or penalties relating to soil contamination [F only]',
                      label:
                        'Fines, enforcement orders and/or penalties relating to soil contamination [F only]'
                    },
                    {
                      value: 'Subsidies for forest-risk commodities [F only]',
                      label: 'Subsidies for forest-risk commodities [F only]'
                    },
                    {
                      value:
                        'Subsidies for fossil fuel exploration and/or extraction [CC only]',
                      label:
                        'Subsidies for fossil fuel exploration and/or extraction [CC only]'
                    },
                    {
                      value:
                        'Subsidies for low-carbon, non-renewable energy projects [CC only]',
                      label:
                        'Subsidies for low-carbon, non-renewable energy projects [CC only]'
                    },
                    {
                      value:
                        'Subsidies for renewable energy projects [CC only]',
                      label: 'Subsidies for renewable energy projects [CC only]'
                    },
                    {
                      value: 'Subsidies on infrastructure',
                      label: 'Subsidies on infrastructure'
                    },
                    {
                      value: 'Subsidies on products or services',
                      label: 'Subsidies on products or services'
                    },
                    {
                      value: 'Sustainable finance',
                      label: 'Sustainable finance'
                    },
                    {
                      value: 'Taxes on products or services',
                      label: 'Taxes on products or services'
                    },
                    {
                      value: 'Water pricing [W only]',
                      label: 'Water pricing [W only]'
                    },
                    {
                      value: 'Other financial mechanisms, please specify',
                      label: 'Other financial mechanisms, please specify'
                    },
                    {value: 'Circular economy', label: 'Circular economy'},
                    {
                      value: 'Deforestation-free products [F only]',
                      label: 'Deforestation-free products [F only]'
                    },
                    {
                      value: 'Extended Producer Responsibility (EPR)',
                      label: 'Extended Producer Responsibility (EPR)'
                    },
                    {
                      value: 'Low environmental impact innovation and R&D',
                      label: 'Low environmental impact innovation and R&D'
                    },
                    {
                      value: 'Recycling and recyclability',
                      label: 'Recycling and recyclability'
                    },
                    {
                      value: 'Sustainable production and consumption',
                      label: 'Sustainable production and consumption'
                    },
                    {
                      value: 'Technology requirements',
                      label: 'Technology requirements'
                    },
                    {
                      value: 'Water use and efficiency [W only]',
                      label: 'Water use and efficiency [W only]'
                    },
                    {
                      value:
                        'Other low-impact production and innovation, please specify',
                      label:
                        'Other low-impact production and innovation, please specify'
                    },
                    {value: 'Food security', label: 'Food security'},
                    {
                      value: 'Land tenure [F only]',
                      label: 'Land tenure [F only]'
                    },
                    {value: 'Public health', label: 'Public health'},
                    {
                      value:
                        'Rights of Indigenous Peoples and local communities',
                      label:
                        'Rights of Indigenous Peoples and local communities'
                    },
                    {
                      value: 'The human right to water and sanitation [W only]',
                      label: 'The human right to water and sanitation [W only]'
                    },
                    {
                      value: 'Other social issues, please specify',
                      label: 'Other social issues, please specify'
                    },
                    {
                      value: 'Traceability requirements',
                      label: 'Traceability requirements'
                    },
                    {
                      value: 'Transparency requirements',
                      label: 'Transparency requirements'
                    },
                    {
                      value: 'Verification and audits',
                      label: 'Verification and audits'
                    },
                    {
                      value: 'Due diligence requirements',
                      label: 'Due diligence requirements'
                    },
                    {
                      value: 'Free, Prior and Informed Consent (FPIC)',
                      label: 'Free, Prior and Informed Consent (FPIC)'
                    },
                    {
                      value:
                        'Collection, availability, and accessibility of forest-related information [F only]',
                      label:
                        'Collection, availability, and accessibility of forest-related information [F only]'
                    },
                    {
                      value:
                        'Public certification schemes for deforestation and conversion [F only]',
                      label:
                        'Public certification schemes for deforestation and conversion [F only]'
                    },
                    {
                      value: 'Corporate environmental reporting',
                      label: 'Corporate environmental reporting'
                    },
                    {
                      value: 'Mandatory environmental reporting',
                      label: 'Mandatory environmental reporting'
                    },
                    {
                      value:
                        'Other transparency and due diligence, please specify',
                      label:
                        'Other transparency and due diligence, please specify'
                    },
                    {
                      value: 'Climate transition plans [CC only]',
                      label: 'Climate transition plans [CC only]'
                    },
                    {
                      value: 'Construction and housing [CC only]',
                      label: 'Construction and housing [CC only]'
                    },
                    {
                      value: 'Corporate environmental targets',
                      label: 'Corporate environmental targets'
                    },
                    {
                      value:
                        'International agreement related to climate change adaptation',
                      label:
                        'International agreement related to climate change adaptation'
                    },
                    {
                      value:
                        'International agreement related to climate change mitigation',
                      label:
                        'International agreement related to climate change mitigation'
                    },
                    {
                      value:
                        'International agreement relating to water- and/or forests-related issues',
                      label:
                        'International agreement relating to water- and/or forests-related issues'
                    },
                    {
                      value:
                        'Small and Medium Forest Enterprises (SMFEs) [F only]',
                      label:
                        'Small and Medium Forest Enterprises (SMFEs) [F only]'
                    },
                    {
                      value: 'Transport infrastructure [CC only]',
                      label: 'Transport infrastructure [CC only]'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q20_4_VALUE,
                  fieldLabel:
                    'Geographic coverage of policy, law, or regulation',
                  options: [
                    {value: 'Global', label: 'Global'},
                    {value: 'Regional', label: 'Regional'},
                    {value: 'National', label: 'National'},
                    {value: 'Subnational', label: 'Subnational'},
                    {value: 'Unknown', label: 'Unknown'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q20_5_VALUE,
                  fieldLabel:
                    'Country/area/region the policy, law, or regulation applies to',
                  options: COUNTRY_OPTIONS,
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q20_4_VALUE,
                    condition: conditionType.NOT,
                    values: ['Regional', 'National', 'Subnational']
                  }
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q20_6_VALUE,
                  fieldLabel:
                    'Your organization’s position on the policy, law, or regulation',
                  options: [
                    {value: 'Oppose', label: 'Oppose'},
                    {value: 'Neutral', label: 'Neutral'},
                    {
                      value: 'Support with no exceptions',
                      label: 'Support with no exceptions'
                    },
                    {
                      value: 'Support with minor exceptions',
                      label: 'Support with minor exceptions'
                    },
                    {
                      value: 'Support with major exceptions',
                      label: 'Support with major exceptions'
                    },
                    {value: 'Undecided', label: 'Undecided'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q20_7_VALUE,
                  fieldLabel:
                    'Details of any exceptions and your organization’s proposed alternative approach to the policy, law, or regulation',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q20_6_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'Oppose',
                      'Support with minor exceptions',
                      'Support with major exceptions'
                    ]
                  }
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q20_8_VALUE,
                  fieldLabel:
                    'Type of direct engagement with policy makers on this policy, law, or regulation',
                  options: [
                    {value: 'Regular meetings', label: 'Regular meetings'},
                    {value: 'Ad-hoc meetings', label: 'Ad-hoc meetings'},
                    {
                      value: 'Discussion in public forums',
                      label: 'Discussion in public forums'
                    },
                    {
                      value:
                        'Participation in working groups organized by policy makers',
                      label:
                        'Participation in working groups organized by policy makers'
                    },
                    {
                      value: 'Participation in voluntary government programs',
                      label: 'Participation in voluntary government programs'
                    },
                    {
                      value: 'Provided funding or in-kind support',
                      label: 'Provided funding or in-kind support'
                    },
                    {
                      value: 'Responding to consultations',
                      label: 'Responding to consultations'
                    },
                    {
                      value: 'Submitting written proposals/inquiries',
                      label: 'Submitting written proposals/inquiries'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q20_9_VALUE,
                  fieldLabel:
                    'Funding figure your organization provided to policy makers in the reporting year relevant to this policy, law, or regulation (currency)',
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q20_10_VALUE,
                  fieldLabel:
                    'Explain the relevance of this policy, law, or regulation to the achievement of your environmental commitments and/or transition plan, how this has informed your engagement, and how you measure the success of your engagement',
                  colSpan: true,
                  textLimit: 2500
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q20_11_VALUE,
                  fieldLabel:
                    'Indicate if youhave evaluated whether your organization’s engagement on this policy, law, or regulation is aligned with global environmental treaties or policy goals',
                  options: [
                    {
                      value: 'Yes, we have evaluated, and it is aligned',
                      label: 'Yes, we have evaluated, and it is aligned'
                    },
                    {
                      value: 'Yes, we have evaluated, and it is not aligned',
                      label: 'Yes, we have evaluated, and it is not aligned'
                    },
                    {
                      value: 'No, we have not evaluated',
                      label: 'No, we have not evaluated'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q20_12_VALUE,
                  fieldLabel:
                    'Global environmental treaties or policy goals aligned with your organization’s engagement on this policy, law or regulation',
                  options: [
                    {
                      value:
                        'Paris Agreement [appears if “Climate change” is selected in column 2]',
                      label:
                        'Paris Agreement [appears if “Climate change” is selected in column 2]'
                    },
                    {
                      value:
                        'Kunming-Montreal Global Biodiversity Framework [appears if “Water” or “Forests” is selected in column 2]',
                      label:
                        'Kunming-Montreal Global Biodiversity Framework [appears if “Water” or “Forests” is selected in column 2]'
                    },
                    {
                      value:
                        'Sustainable Development Goal 6 on Clean Water and Sanitation [appears if “Water” is selected in column 2]',
                      label:
                        'Sustainable Development Goal 6 on Clean Water and Sanitation [appears if “Water” is selected in column 2]'
                    },
                    {
                      value:
                        'Another global environmental treaty or policy goal, please specify',
                      label:
                        'Another global environmental treaty or policy goal, please specify'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q20_11_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'Yes, we have evaluated, and it is aligned',
                      'Yes, we have evaluated, and it is not aligned'
                    ]
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.11.2 Provide details of your indirect engagement on policy, law, or regulation that may (positively or negatively) impact the environment through trade associations or other intermediary organizations or individuals in the reporting year.',
            tooltip: `Organizations have many potential avenues for engagement activities. Trade associations are a tool through which organizations can shape policy and interact with legislators and industry peers. Engaging with, or providing support to, other intermediary organizations or individuals can play an important role in the development and adoption of environmental policy. As such, data users expect organizations to be transparent about the full range of their engagement and
                     funding activities as well as their relationship and responsibilities with intermediary organizations that are likely to take a position on legislation or that could influence policy, law, or regulation that may impact the environment.`,
            conditionalVisibility: {
              dependsOn:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_VALUE,
              showWhen: [
                'Yes, we engaged indirectly through, and/or provided financial or in-kind support to a trade association or other intermediary organization or individual whose activities could influence policy, law, or regulation'
              ],
              condition: conditionType.EQUALS,
              fieldName:
                CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_1_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q21_VALUE,
            dependsOn:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_1_VALUE,
            showWhen: [
              'Yes, we engaged indirectly through, and/or provided financial or in-kind support to a trade association or other intermediary organization or individual whose activities could influence policy, law, or regulation'
            ],
            relatedTableKey:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q19_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q21_1_VALUE,
                  fieldLabel: 'Type of indirect engagement',
                  options: [
                    {
                      value: 'Indirect engagement via a trade association',
                      label: 'Indirect engagement via a trade association'
                    },
                    {
                      value:
                        'Indirect engagement via other intermediary organization or individual',
                      label:
                        'Indirect engagement via other intermediary organization or individual'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q21_2_VALUE,
                  fieldLabel: 'Type of organization or individual',
                  options: [
                    {
                      value: 'Governmental institution',
                      label: 'Governmental institution'
                    },
                    {
                      value: 'Independent consultant',
                      label: 'Independent consultant'
                    },
                    {
                      value: 'International Governmental Organization (IGO)',
                      label: 'International Governmental Organization (IGO)'
                    },
                    {
                      value:
                        'Non-Governmental Organization (NGO) or charitable organization',
                      label:
                        'Non-Governmental Organization (NGO) or charitable organization'
                    },
                    {value: 'Private company', label: 'Private company'},
                    {
                      value: 'Publicly-listed company',
                      label: 'Publicly-listed company'
                    },
                    {
                      value: 'Research organization',
                      label: 'Research organization'
                    },
                    {value: 'Start-up company', label: 'Start-up company'},
                    {
                      value:
                        'State-Owned Enterprise (SOE)/Government-Owned Corporation (GOC)',
                      label:
                        'State-Owned Enterprise (SOE)/Government-Owned Corporation (GOC)'
                    },
                    {
                      value: 'Trust or foundation',
                      label: 'Trust or foundation'
                    },
                    {
                      value: 'University or other educational institution',
                      label: 'University or other educational institution'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q21_1_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'Indirect engagement via other intermediary organization or individual'
                    ]
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q21_3_VALUE,
                  fieldLabel:
                    'State the organization or position of individual',
                  colSpan: true,
                  textLimit: 500
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q21_4_VALUE,
                  fieldLabel: 'Trade association',
                  options: TRADE_ASSOCIATION_OPTIONS,
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q21_1_VALUE,
                    condition: conditionType.NOT,
                    values: ['Indirect engagement via a trade association']
                  }
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q21_5_VALUE,
                  fieldLabel:
                    'Environmental issues relevant to the policies, laws, or regulations on which the organization or individual has taken a position',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Water', label: 'Water'},
                    {value: 'Forests', label: 'Forests'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q21_6_VALUE,
                  fieldLabel:
                    'Indicate whether your organization’s position is consistent with the organization or individual you engage with',
                  options: [
                    {value: 'Consistent', label: 'Consistent'},
                    {value: 'Inconsistent', label: 'Inconsistent'},
                    {value: 'Mixed', label: 'Mixed'},
                    {value: 'Unknown', label: 'Unknown'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q21_7_VALUE,
                  fieldLabel:
                    'Indicate whether your organization attempted to influence the organization or individual’s position in the reporting year',
                  options: [
                    {
                      value: 'Yes, and they have changed their position',
                      label: 'Yes, and they have changed their position'
                    },
                    {
                      value:
                        'Yes, we attempted to influence them but they did not change their position',
                      label:
                        'Yes, we attempted to influence them but they did not change their position'
                    },
                    {
                      value: 'Yes, we publicly promoted their current position',
                      label: 'Yes, we publicly promoted their current position'
                    },
                    {
                      value: 'Yes, we publicly opposed their current position',
                      label: 'Yes, we publicly opposed their current position'
                    },
                    {
                      value:
                        'Yes, we terminated our funding/membership in the reporting year',
                      label:
                        'Yes, we terminated our funding/membership in the reporting year'
                    },
                    {
                      value:
                        'Yes, we decided to terminate our funding/membership within the next two years',
                      label:
                        'Yes, we decided to terminate our funding/membership within the next two years'
                    },
                    {
                      value:
                        'No, we did not attempt to influence their position',
                      label:
                        'No, we did not attempt to influence their position'
                    },
                    {
                      value:
                        'No, we do not know their position [only appears if “Unknown” is selected in column 6]',
                      label:
                        'No, we do not know their position [only appears if “Unknown” is selected in column 6]'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q21_8_VALUE,
                  fieldLabel:
                    'Describe how your organization’s position is consistent with or differs from the organization or individual’s position, and any actions taken to ',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q21_6_VALUE,
                    condition: conditionType.NOT,
                    values: ['Consistent', 'Inconsistent', 'Mixed']
                  }
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q21_9_VALUE,
                  fieldLabel:
                    'Funding figure your organization provided to this organization or individual in the reporting year (currency)',
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q21_10_VALUE,
                  fieldLabel:
                    'Describe the aim of this funding and how it could influence policy, law or regulation that may impact the environment',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q21_9_VALUE,
                    condition: conditionType.GREATER_THAN,
                    numericValue: 0
                  }
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q21_11_VALUE,
                  fieldLabel:
                    'Indicate if you have evaluated whether your organization’s engagement is aligned with global environmental ',
                  options: [
                    {
                      value: 'Yes, we have evaluated, and it is aligned',
                      label: 'Yes, we have evaluated, and it is aligned'
                    },
                    {
                      value: 'Yes, we have evaluated, and it is not aligned',
                      label: 'Yes, we have evaluated, and it is not aligned'
                    },
                    {
                      value: 'No, we have not evaluated',
                      label: 'No, we have not evaluated'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q21_12_VALUE,
                  fieldLabel:
                    'Global environmental treaties or policy goals aligned with your organization’s engagement on this policy, law or regulation',
                  options: [
                    {
                      value:
                        'Paris Agreement [appears if “Climate change” is selected in column 2]',
                      label:
                        'Paris Agreement [appears if “Climate change” is selected in column 2]'
                    },
                    {
                      value:
                        'Kunming-Montreal Global Biodiversity Framework [appears if “Water” or “Forests” is selected in column 2]',
                      label:
                        'Kunming-Montreal Global Biodiversity Framework [appears if “Water” or “Forests” is selected in column 2]'
                    },
                    {
                      value:
                        'Sustainable Development Goal 6 on Clean Water and Sanitation [appears if “Water” is selected in column 2]',
                      label:
                        'Sustainable Development Goal 6 on Clean Water and Sanitation [appears if “Water” is selected in column 2]'
                    },
                    {
                      value:
                        'Another global environmental treaty or policy goal, please specify',
                      label:
                        'Another global environmental treaty or policy goal, please specify'
                    }
                  ],
                  colSpan: true
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.12 Have you published information about your organization’s response to environmental issues for this reporting year in places other than your CDP response?',
            tooltip:
              'The integration of information on environmental issues into mainstream financial reporting is a regulatory requirement in some jurisdictions and is a TCFD recommendation. Data users wish to understand whether an organization includes or plans to include environmental information in their mainstream reports to facilitate their understanding of the organization’s response to environmental dependencies, impacts, risks, and opportunities.'
          },
          {
            fieldType: inputType.SELECT,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q22_VALUE,
            options: PROCESS_PLACE_OPTIONS,
            colSpan: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '4.12.1 Provide details on the information published about your organization’s response to environmental issues for this reporting year in places other than your CDP response. Please attach the publication.',
            tooltip:
              'For transparency in corporate environmental reporting, organizations are encouraged to integrate non-financial metrics and data into mainstream financial reports. Data users seek to understand where and how organizations communicate their environmental strategies, their emissions figures, and their dependencies, impacts, risks, and opportunities, as well as whether these communications are in line with environmental disclosure standards or frameworks.'
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q23_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q23_1_VALUE,
                  fieldLabel: 'Publication',
                  options: [
                    {
                      value: 'In mainstream reports',
                      label: 'In mainstream reports'
                    },
                    {
                      value:
                        'In mainstream reports, in line with environmental disclosure standards or frameworks',
                      label:
                        'In mainstream reports, in line with environmental disclosure standards or frameworks'
                    },
                    {
                      value: 'In other regulatory filings',
                      label: 'In other regulatory filings'
                    },
                    {
                      value: 'In voluntary communications',
                      label: 'In voluntary communications'
                    },
                    {
                      value: 'In voluntary sustainability reports',
                      label: 'In voluntary sustainability reports'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q23_2_VALUE,
                  fieldLabel:
                    'Standard or framework the report is in line with',
                  options: [
                    {value: 'ESRS', label: 'ESRS'},
                    {value: 'GRI', label: 'GRI'},
                    {value: 'IFRS', label: 'IFRS'},
                    {value: 'TCFD', label: 'TCFD'},
                    {value: 'TNFD', label: 'TNFD'},
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q23_1_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'In mainstream reports, in line with environmental disclosure standards or frameworks'
                    ]
                  }
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q23_3_VALUE,
                  fieldLabel: 'Environmental issues covered in publication',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'},
                    {value: 'Biodiversity', label: 'Biodiversity'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q23_4_VALUE,
                  fieldLabel: 'Status of publication',
                  options: [
                    {value: 'Complete', label: 'Complete'},
                    {
                      value: 'Underway - previous year attached',
                      label: 'Underway - previous year attached'
                    },
                    {
                      value: 'Underway - this is our first year',
                      label: 'Underway - this is our first year'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q23_5_VALUE,
                  fieldLabel: 'Content elements',
                  options: [
                    {
                      value: 'Content of environmental policies',
                      label: 'Content of environmental policies'
                    },
                    {value: 'Governance', label: 'Governance'},
                    {
                      value: 'Public policy engagement',
                      label: 'Public policy engagement'
                    },
                    {
                      value: 'Dependencies & Impacts',
                      label: 'Dependencies & Impacts'
                    },
                    {
                      value: 'Risks & Opportunities',
                      label: 'Risks & Opportunities'
                    },
                    {value: 'Strategy', label: 'Strategy'},
                    {
                      value: 'Value chain engagement',
                      label: 'Value chain engagement'
                    },
                    {
                      value: 'Biodiversity indicators [B only]',
                      label: 'Biodiversity indicators [B only]'
                    },
                    {
                      value: 'Emissions figures [CC only]',
                      label: 'Emissions figures [CC only]'
                    },
                    {
                      value: 'Emission targets [CC only]',
                      label: 'Emission targets [CC only]'
                    },
                    {
                      value:
                        'Deforestation- and conversion-free (DCF) status metrics [F only]',
                      label:
                        'Deforestation- and conversion-free (DCF) status metrics [F only]'
                    },
                    {
                      value: 'Deforestation and conversion footprint [F only]',
                      label: 'Deforestation and conversion footprint [F only]'
                    },
                    {
                      value: 'Commodity volumes [F only]',
                      label: 'Commodity volumes [F only]'
                    },
                    {
                      value: 'Water accounting figures [W only]',
                      label: 'Water accounting figures [W only]'
                    },
                    {
                      value: 'Water pollution indicators [W only]',
                      label: 'Water pollution indicators [W only]'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q23_6_VALUE,
                  fieldLabel: 'Page/section reference',
                  colSpan: true,
                  textLimit: 500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q23_4_VALUE,
                    condition: conditionType.NOT,
                    values: ['Complete', 'Underway - previous year attached']
                  }
                },
                {
                  type: inputType.FILEUPLOAD,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q23_7_VALUE,
                  fieldLabel: 'Attach the relevant publication',
                  colSpan: true,
                  fileUploadAction:
                    API_ENDPOINT_JSON.ESG.CDP_CORPORATE.documentUpload
                      .governance,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q23_4_VALUE,
                    condition: conditionType.NOT,
                    values: ['Complete', 'Underway - previous year attached']
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_GOVERNANCE_FIELD_NAMES.CORPORATE_GOVERNANCE_Q23_8_VALUE,
                  fieldLabel: 'Comment',
                  colSpan: true,
                  textLimit: 1500
                }
              ]
            }
          }
        ]
      }
    ]
  }
];
